import { isDrawableByPdfMake } from "../../infrastructure/documents/pdf-image.js";
import { createIcons, Save, Eye, CheckCircle, FileDown, History, RefreshCw, Calculator, PenLine, Upload, Trash2, ExternalLink, Send, ClipboardList, Image as ImageIcon } from "lucide";
import { contextStore } from "../auth/context-store.js";
import { sessionRequestContext } from "../auth/request-context.js";
import { aiReviewSlot, bindAiReviewHost } from "../components/ai-review-host.js";
import { PermissionCode } from "../../permissions/permissions.js";
import { router } from "../router/router.js";
import { AppError } from "../../shared/errors/app-error.js";
import { CLIENT_WITHOUT_PLH } from "../../application/services/project-responsibility-service.js";
import { openClientPlhSetup } from "../navigation/client-plh-deep-link.js";
import {
  AVR_CRITICALITY_LABELS,
  AVR_HISTORY_LABELS,
  AVR_SITUATION_LABELS,
  AVR_SOURCE_KIND_LABELS,
  AVR_STATUS_LABELS,
  AVR_GENERAL_STATUS_OPTIONS,
  isAvrGeneralStatus,
  AVR_POINT_HISTORY_ORIGIN_LABELS,
  correlationKey,
  findCriticalPoint,
  formatCivilDate,
  formatNumberBR,
  formatThicknessInput,
  validatePointHistoryInput,
  type AvrReport,
  type AvrSignatory,
  type AvrSourceReport
} from "../../domain/engineering/avr-report-domain.js";
import type { AvrSourceRef } from "../../application/services/avr-report-service.js";
import {
  applyAvrSourceFilters,
  avrWorkflowActions,
  buildAvrSeedFromContext,
  canCreateAvrFromContext,
  elaboratorCandidates,
  filterEquipmentsByContext,
  formatSignatoryOption,
  missingAvrContextFields,
  plhCandidatesForClient,
  staleAvrContextFields,
  statusBadgeClass,
  type AvrContextSelection,
  type AvrSourceFilterState,
  AVR_PLH_QUEUE_STATUSES
} from "./avr-page-context.js";
import "../components/document/pdf-preview.js";
import { PLH_REVIEW_UI_STYLES } from "../components/plh-review-ui.js";
import { CONTEXT_LOAD_STYLES, ContextLoadTracker, loadList } from "../context/context-load.js";
import { CONTEXT_HYDRATION_STYLES, hydrateDocumentContext, hydrationBanner, type HydrationReport } from "../context/context-hydration.js";
import { autosaveDelayMs, loadOperationalSettings } from "../settings/operational-settings.js";

const EQUIPMENT_TYPE_LABELS: Record<string, string> = {
  PRESSURE_VESSEL: "Vaso de Pressão (VP)",
  STORAGE_TANK: "Tanque de Armazenamento (TQ)",
  HEAT_EXCHANGER: "Permutador de Calor (TC)",
  BOILER: "Caldeira (CD)",
  FURNACE: "Forno Industrial (FN)",
  PIPING_SYSTEM: "Sistema de Tubulação (ST)",
  PIPING_LINE: "Tubulação",
  OTHER: "Outro Equipamento"
};

const SECTIONS: Array<[string, string]> = [
  ["01", "Contexto"],
  ["02", "Fontes de Dados"],
  ["03", "Dados Importados"],
  ["04", "Dados Complementares"],
  ["05", "Taxa de Corrosão"],
  ["06", "Vida Remanescente"],
  ["07", "Análise Técnica"],
  ["08", "Recomendações"],
  ["09", "Croqui"],
  ["10", "Signatários"]
];

const RATE_BASIS_OPTIONS: Array<[AvrReport["rateBasis"], string]> = [
  ["HIGHEST", "Maior entre curto e longo prazo (referência conservadora API 570)"],
  ["SHORT_TERM", "Curto prazo"],
  ["LONG_TERM", "Longo prazo"]
];

const MAX_IMAGE_BYTES = 4 * 1024 * 1024;

/**
 * AVR — AVALIAÇÃO DE VIDA REMANESCENTE.
 *
 * Renderizador fino sobre AvrReportService: espessuras só chegam pelas
 * fontes (ME / B-Scan) e ficam somente leitura; a taxa e a vida vêm do motor
 * API 570 através do serviço. Nenhum número é digitado nem calculado aqui.
 */
class AppAvrPage extends HTMLElement {
  /**
   * Tratamento único das leituras de contexto (política em
   * presentation/context/context-load.ts). Um erro de permissão deixa de
   * virar lista vazia e de produzir "nenhum cadastro" com o cadastro
   * existente na base.
   */
  private readonly ctxLoad = new ContextLoadTracker();

  private report: AvrReport | null = null;
  private isLoading = true;
  private loadError: string | null = null;
  private isSaving = false;
  private isWorking = false;
  private saveNotice: string | null = null;
  private noticeKind: "info" | "ok" | "error" = "info";
  private validationErrors: string[] | null = null;
  private validationOk = false;

  private professionals: any[] = [];
  private signatureImages = new Map<string, string | null>();
  private workOrders: any[] = [];
  private subWorkOrders: any[] = [];
  private recommendations: Array<{ id: string; zr: string; scope: string; status: string }> = [];

  private availableSources: AvrSourceReport[] = [];
  private sourceFilters: AvrSourceFilterState = { kind: "ALL", from: "", to: "" };
  private pendingSources = new Set<string>();

  // CONTEXTO
  private clients: any[] = [];
  private projects: any[] = [];
  private unitAreas: any[] = [];
  private equipments: any[] = [];
  private existingReports: AvrReport[] = [];
  private selection: AvrContextSelection = { clientId: "", projectId: "", unitAreaId: "", equipmentId: "", inspectionId: "" };
  /** Desfecho da reposição do contexto gravado — avisa o que não voltou. */
  private hydration: HydrationReport | null = null;
  private contextError: string | null = null;
  private isResolvingContext = false;

  private previewSnapshot: any | null = null;
  private previewOpen = false;
  private historyOpen = false;
  /** Modal "ENVIAR AVR PARA ANÁLISE DO PLH?" — aberto só depois da validação completa passar. */
  private submitModal: { open: boolean; plhProfessionalId: string; candidates: Array<{ id: string; name: string; functionTitle: string; credential: string }>; sending: boolean; resolved: { plh: { professionalId: string; name: string; functionTitle: string; credential: string } | null; engineer: { professionalId: string; name: string } | null; project: { id: string; code: string; name: string } | null; reason: string } | null } = { open: false, plhProfessionalId: "", candidates: [], sending: false, resolved: null };
  /** Modal "HISTÓRICO DO PONTO Pxx": Espessura Anterior + Data da Medição Anterior, por ponto. */
  /** Modal "HISTÓRICO DO PONTO Pxx": dois blocos independentes — CURTO PRAZO e LONGO PRAZO (espessura + data cada). */
  private historyModal: { open: boolean; key: string; short: { thickness: string; date: string; ref: string }; long: { thickness: string; date: string; ref: string }; errors: string[]; search: "SHORT" | "LONG" | null } = { open: false, key: "", short: { thickness: "", date: "", ref: "" }, long: { thickness: "", date: "", ref: "" }, errors: [], search: null };
  private deleteModal = false;
  private activeSection = "01";
  private lightbox: { open: boolean; src: string; title: string } = { open: false, src: "", title: "" };

  private autosaveTimer: ReturnType<typeof setTimeout> | null = null;
  private boundFormInput = this.handleFormInput.bind(this);

  connectedCallback() {
    // INSPECT IA REVIEW: a revisão aplicou texto ⇒ relê a AVR do serviço.
    bindAiReviewHost(this, async () => {
      if (!this.report) return;
      const fresh = await this.svc.getReport(this.scopedCtx(this.report.clientId), this.report.id).catch(() => null);
      if (fresh) { this.report = fresh; this.render(); }
    });
    this.addEventListener("input", this.boundFormInput);
    this.addEventListener("change", this.boundFormInput);
    this.init();
  }

  disconnectedCallback() {
    this.removeEventListener("input", this.boundFormInput);
    this.removeEventListener("change", this.boundFormInput);
    if (this.autosaveTimer) clearTimeout(this.autosaveTimer);
  }

  private get svc() { return window.app.platform.avrReportService; }

  // ------------------------------------------------------------ CONTEXT

  private buildCtx(): any {
    const c = contextStore.get();
    return { activeClientId: c.activeClientId || undefined, permissions: c.permissions, correlationId: "", userId: c.userId, professionalId: c.professionalId || null };
  }

  private scopedCtx(clientId?: string): any {
    const base = this.buildCtx();
    const target = clientId ?? this.selection.clientId ?? this.report?.clientId;
    return target ? { ...base, activeClientId: target } : base;
  }

  private async init() {
    this.isLoading = true;
    this.render();
    await loadOperationalSettings();
    const id = this.getAttribute("id");
    try {
      this.professionals = (await this.ctxLoad.load("Profissionais", () => window.app.platform.adminService.listProfessionals(this.buildCtx()))) || [];
      if (id) {
        const existing = await this.svc.getReport(this.buildCtx(), id);
        if (!existing) {
          this.loadError = "AVR não encontrada.";
          this.isLoading = false;
          this.render();
          return;
        }
        await this.openReport(existing);
        return;
      }
      await this.loadClients();
      this.isLoading = false;
      this.render();
    } catch (e: any) {
      console.error("[AVR] Falha ao carregar página", e);
      this.loadError = this.messageOf(e, "Erro ao carregar a AVR.");
      this.isLoading = false;
      this.render();
    }
  }

  /**
   * HIDRATAÇÃO DO CONTEXTO DE UMA AVR JÁ GRAVADA. Mesmo defeito das demais:
   * só o Cliente voltava, e o primeiro toque em qualquer campo disparava a
   * cascata, apagando Projeto, Unidade/Área e Ativo gravados. A AVR não tem
   * nível de Inspeção — ela se apoia nos relatórios-fonte.
   */
  private async hydrateContextFromReport() {
    const report = this.report;
    if (!report) return;
    const ctx = () => this.buildCtx();

    this.hydration = await hydrateDocumentContext([
      {
        label: "o Cliente",
        persistedId: report.clientId,
        load: () => loadList<any>("os Clientes", () => window.app.platform.adminService.listAuthorizedClients(ctx())),
        apply: (value, options) => { this.selection.clientId = value || String(report.clientId); this.clients = [...options]; }
      },
      {
        label: "o Projeto",
        persistedId: report.projectId,
        load: () => loadList<any>("os Projetos", () => window.app.platform.projectService.listProjects(this.selection.clientId, undefined, sessionRequestContext())),
        apply: (value, options) => { this.selection.projectId = value; this.projects = [...options]; }
      },
      {
        label: "a Unidade / Área",
        persistedId: report.unitAreaId,
        load: () => loadList<any>("as Unidades / Áreas", () => window.app.platform.projectService.listUnitAreas({ projectId: this.selection.projectId, clientId: this.selection.clientId }, undefined, sessionRequestContext())),
        apply: (value, options) => { this.selection.unitAreaId = value; this.unitAreas = [...options]; }
      },
      {
        label: "o Equipamento / TAG",
        persistedId: report.equipmentId,
        // Cliente DO DOCUMENTO, nunca o da sessão (CLAUDE.md §28).
        load: () => loadList<any>("os Equipamentos", () => window.app.platform.equipmentService.listEquipments(ctx(), { clientId: report.clientId })),
        apply: (value, options) => { this.selection.equipmentId = value; this.equipments = [...options]; }
      }
    ]);
  }

  private async openReport(report: AvrReport) {
    this.report = report;
    await this.hydrateContextFromReport();
    this.pendingSources = new Set(report.selectedSources.map(s => this.sourceKey(s)));
    await Promise.all([this.loadSources(), this.loadWorkOrdersForReport(), this.loadRecommendations(), this.loadSignatureImages()]);
    this.isLoading = false;
    this.render();
  }

  private sourceKey(s: { kind: string; reportId: string }): string { return `${s.kind}|${s.reportId}`; }

  private async loadSources() {
    this.availableSources = [];
    if (!this.report?.equipmentId) return;
    try {
      this.availableSources = await this.svc.listAvailableSources(this.scopedCtx(this.report.clientId), this.report.clientId, String(this.report.equipmentId));
    } catch (e) {
      console.error("[AVR] Falha ao listar relatórios de origem", e);
    }
  }

  private async loadWorkOrdersForReport() {
    if (!this.report) return;
    this.workOrders = await window.app.platform.adminService
      .listWorkOrdersForDocument(this.buildCtx(), this.report.clientId, this.report.projectId || undefined)
      .catch(() => []);
    await this.loadSubWorkOrdersForReport();
  }

  private async loadSubWorkOrdersForReport() {
    this.subWorkOrders = [];
    if (!this.report?.osWorkOrderId) return;
    this.subWorkOrders = await window.app.platform.adminService
      .listSubWorkOrdersForDocument(this.buildCtx(), this.report.osWorkOrderId)
      .catch(() => []);
  }

  private async loadRecommendations() {
    this.recommendations = [];
    if (!this.report) return;
    try {
      this.recommendations = await this.svc.listEquipmentRecommendations(this.scopedCtx(this.report.clientId), this.report.id);
    } catch (e) {
      console.error("[AVR] Falha ao listar Recommendations do equipamento", e);
    }
  }

  private async loadSignatureImages() {
    if (!this.report) return;
    const ids = this.report.signatories.map(s => s.professionalId).filter((id): id is any => !!id);
    await Promise.all(ids.map(async id => {
      if (this.signatureImages.has(id)) return;
      try {
        const image = await window.app.platform.adminService.getProfessionalSignature(this.buildCtx(), id);
        this.signatureImages.set(id, isDrawableByPdfMake(image) ? image : null);
      } catch {
        this.signatureImages.set(id, null);
      }
    }));
  }

  // --- Cliente → Projeto → Unidade/Área → Equipamento ---

  private async loadClients() {
    this.clients = (await this.ctxLoad.load("Clientes", () => window.app.platform.adminService.listAuthorizedClients(this.buildCtx()))) || [];
    const preselected = contextStore.get().activeClientId || "";
    if (preselected && this.clients.some((c: any) => c.id === preselected)) {
      this.selection.clientId = preselected;
      await this.loadProjects();
      await this.loadEquipments();
    }
  }

  private async loadProjects() {
    this.projects = []; this.unitAreas = []; this.equipments = []; this.existingReports = [];
    if (!this.selection.clientId) return;
    this.projects = (await this.ctxLoad.load("Projetos", () => window.app.platform.projectService.listProjects(this.selection.clientId, undefined, sessionRequestContext()))) || [];
  }

  private async loadUnitAreas() {
    this.unitAreas = []; this.equipments = []; this.existingReports = [];
    if (!this.selection.projectId) return;
    this.unitAreas = (await window.app.platform.projectService
      .listUnitAreas({ projectId: this.selection.projectId, clientId: this.selection.clientId })
      .catch(() => [])) || [];
  }

  private async loadEquipments() {
    this.equipments = []; this.existingReports = [];
    if (!this.selection.clientId) return;
    const all = await window.app.platform.equipmentService
      .listEquipments(this.scopedCtx(), { clientId: this.selection.clientId })
      .catch(() => []);
    this.equipments = filterEquipmentsByContext(all || [], this.selection);
  }

  private async loadExistingReports() {
    this.existingReports = [];
    if (!this.selection.equipmentId) return;
    try {
      this.existingReports = [...await this.svc.listByEquipment(this.scopedCtx(), this.selection.equipmentId)];
    } catch (e) {
      console.error("[AVR] Falha ao listar AVRs do equipamento", e);
    }
  }

  private async onContextChange(field: keyof AvrContextSelection, value: string) {
    this.contextError = null;
    this.selection[field] = value;
    if (field === "clientId") {
      this.selection.projectId = ""; this.selection.unitAreaId = ""; this.selection.equipmentId = "";
      await this.loadProjects();
      await this.loadEquipments();
    } else if (field === "projectId") {
      this.selection.unitAreaId = ""; this.selection.equipmentId = "";
      await this.loadUnitAreas();
      await this.loadEquipments();
    } else if (field === "unitAreaId") {
      this.selection.equipmentId = "";
      await this.loadEquipments();
    } else if (field === "equipmentId") {
      await this.loadExistingReports();
    }
    this.render();
  }

  private async onCreateFromContext() {
    if (this.isResolvingContext) return;
    const missing = missingAvrContextFields(this.selection);
    if (missing.length > 0) {
      this.contextError = `Selecione ${missing.join(", ")} para criar a AVR.`;
      this.render();
      return;
    }
    const stale = staleAvrContextFields(this.selection, { clients: this.clients, projects: this.projects, unitAreas: this.unitAreas, equipments: this.equipments });
    if (stale.length > 0) {
      this.contextError = `Selecione novamente ${stale.join(", ")} — o contexto foi recarregado.`;
      this.render();
      return;
    }
    this.isResolvingContext = true;
    this.render();
    try {
      const seed = buildAvrSeedFromContext({
        selection: this.selection,
        client: this.clients.find((c: any) => c.id === this.selection.clientId),
        project: this.projects.find((p: any) => p.id === this.selection.projectId),
        unitArea: this.unitAreas.find((u: any) => u.id === this.selection.unitAreaId),
        equipment: this.equipments.find((e: any) => e.id === this.selection.equipmentId),
        equipmentTypeLabels: EQUIPMENT_TYPE_LABELS
      });
      const created = await this.svc.createDraft(this.scopedCtx(), seed);
      this.isResolvingContext = false;
      window.history.replaceState(null, "", `/integrity/avr/${created.id}`);
      await this.openReport(created);
    } catch (e: any) {
      console.error("[AVR] Falha ao criar AVR", e);
      this.isResolvingContext = false;
      this.contextError = this.messageOf(e, "Erro ao criar a AVR.");
      this.render();
    }
  }

  private async onOpenExisting(id: string) {
    const existing = this.existingReports.find(r => r.id === id);
    if (!existing) return;
    window.history.replaceState(null, "", `/integrity/avr/${existing.id}`);
    await this.openReport(existing);
  }

  private async onChangeContext() {
    this.report = null; this.loadError = null; this.saveNotice = null; this.validationErrors = null; this.contextError = null;
    this.selection = { clientId: "", projectId: "", unitAreaId: "", equipmentId: "", inspectionId: "" };
    this.projects = []; this.unitAreas = []; this.equipments = []; this.existingReports = [];
    this.removeAttribute("id");
    window.history.replaceState(null, "", "/integrity/avr");
    this.isLoading = true;
    this.render();
    await this.loadClients();
    this.isLoading = false;
    this.render();
  }

  // ---------------------------------------------------------- MUTATIONS

  private scheduleAutosave() {
    if (this.autosaveTimer) clearTimeout(this.autosaveTimer);
    // CONFIGURAÇÕES → GERAL: autosave desligado = só grava em "Salvar rascunho".
    const delay = autosaveDelayMs();
    if (delay === null) return;
    this.autosaveTimer = setTimeout(() => {
      this.persistNow().catch(e => this.notify(this.messageOf(e, "Não foi possível salvar o rascunho."), "error"));
    }, delay);
  }

  private async persistNow(): Promise<void> {
    if (!this.report || this.report.status === "SIGNED") return;
    this.isSaving = true;
    try {
      this.report = await this.svc.saveDraft(this.scopedCtx(this.report.clientId), this.report);
    } finally {
      this.isSaving = false;
    }
  }

  private handleFormInput(e: Event) {
    if (!this.report || this.report.status === "SIGNED") return;
    const target = e.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
    if (!target || !("dataset" in target)) return;
    const ds = target.dataset;

    if (ds["comp"]) {
      const field = ds["comp"]!;
      if (field === "minimumThicknessAdoptedMm") {
        const raw = target.value.trim().replace(",", ".");
        const parsed = raw === "" ? null : Number(raw);
        this.report.complementary.minimumThicknessAdoptedMm = parsed !== null && Number.isFinite(parsed) ? parsed : null;
      } else {
        (this.report.complementary as any)[field] = target.value;
      }
      this.scheduleAutosave();
      return;
    }
    if (ds["concl"]) {
      (this.report.conclusion as any)[ds["concl"]!] = target.value;
      this.scheduleAutosave();
      return;
    }
    if (ds["sigRole"] && ds["sigField"]) {
      const s = this.ensureSignatory(ds["sigRole"] as AvrSignatory["role"]);
      (s as any)[ds["sigField"]!] = target.value;
      this.scheduleAutosave();
      return;
    }
    if (ds["field"] && ds["managed"] !== "true") {
      (this.report as any)[ds["field"]!] = target.value;
      this.scheduleAutosave();
    }
  }

  private ensureSignatory(role: AvrSignatory["role"]): AvrSignatory {
    const r = this.report!;
    let s = r.signatories.find(x => x.role === role);
    if (!s) {
      s = { role, professionalId: null, name: "", functionTitle: "", credential: "", signatureImageId: null, signedAt: null };
      r.signatories.push(s);
    }
    return s;
  }

  private credentialFor(p: any): string {
    return p.crea || p.cft || p.snqc || p.sncc || p.endIso9712 || p.endIso || "";
  }

  private async onSignatoryProfessionalChange(role: AvrSignatory["role"], professionalId: string) {
    if (!this.report) return;
    const prof = this.professionals.find((p: any) => p.id === professionalId);
    const s = this.ensureSignatory(role);
    if (prof) {
      s.professionalId = prof.id;
      s.name = prof.name || "";
      s.functionTitle = prof.functionTitle || "";
      s.credential = this.credentialFor(prof);
      s.signatureImageId = prof.signatureImageId || null;
    } else {
      s.professionalId = null; s.name = ""; s.functionTitle = ""; s.credential = ""; s.signatureImageId = null;
    }
    await this.loadSignatureImages();
    this.scheduleAutosave();
    this.render();
  }

  private async onWorkOrderChange(workOrderId: string) {
    if (!this.report) return;
    const wo = this.workOrders.find((w: any) => w.id === workOrderId);
    this.report.osWorkOrderId = workOrderId || null;
    this.report.osNumber = wo?.code || "";
    this.report.osSubWorkOrderId = null;
    this.report.subOsNumber = "";
    await this.loadSubWorkOrdersForReport();
    this.scheduleAutosave();
    this.render();
  }

  private onSubWorkOrderChange(subWorkOrderId: string) {
    if (!this.report) return;
    const sub = this.subWorkOrders.find((s: any) => s.id === subWorkOrderId);
    this.report.osSubWorkOrderId = subWorkOrderId || null;
    this.report.subOsNumber = sub?.code || "";
    this.scheduleAutosave();
    this.render();
  }

  // ------------------------------------------------------------- ACTIONS

  private async run(action: () => Promise<void>, fallback: string) {
    if (this.isWorking) return;
    this.isWorking = true;
    try {
      await action();
    } catch (e: any) {
      console.error("[AVR]", fallback, e);
      this.saveNotice = this.messageOf(e, fallback);
      this.noticeKind = "error";
    } finally {
      this.isWorking = false;
      this.render();
    }
  }

  private onSaveDraft() {
    return this.run(async () => {
      await this.persistNow();
      this.saveNotice = "Rascunho salvo com sucesso.";
      this.noticeKind = "ok";
      this.validationErrors = null;
    }, "Erro ao salvar o rascunho.");
  }

  /** Aplica as fontes marcadas: o serviço reimporta as leituras e marca o cálculo como desatualizado. */
  private onApplySources() {
    return this.run(async () => {
      if (!this.report) return;
      await this.persistNow();
      const refs: AvrSourceRef[] = this.availableSources
        .filter(s => this.pendingSources.has(this.sourceKey(s)))
        .map(s => ({ kind: s.kind, reportId: s.reportId }));
      this.report = await this.svc.selectSources(this.scopedCtx(this.report.clientId), this.report.id, refs);
      await this.loadSubWorkOrdersForReport();
      this.saveNotice = refs.length === 0
        ? "Nenhuma fonte selecionada."
        : `${refs.length} relatório(s) de origem aplicado(s) — ${this.report.readings.length} leitura(s) importada(s).${this.report.calculationOutdated ? " O cálculo anterior ficou DESATUALIZADO: execute RECALCULAR." : ""}`;
      this.noticeKind = this.report.calculationOutdated ? "info" : "ok";
      this.validationErrors = null;
    }, "Erro ao aplicar as fontes.");
  }

  private onRefreshSources() {
    return this.run(async () => {
      if (!this.report) return;
      await this.persistNow();
      this.report = await this.svc.refreshSources(this.scopedCtx(this.report.clientId), this.report.id);
      await this.loadSources();
      this.pendingSources = new Set(this.report.selectedSources.map(s => this.sourceKey(s)));
      const last = this.report.history[this.report.history.length - 1];
      this.saveNotice = last?.detail || "Dados das fontes atualizados.";
      this.noticeKind = this.report.calculationOutdated ? "info" : "ok";
    }, "Erro ao atualizar os dados das fontes.");
  }

  private onCalculate() {
    return this.run(async () => {
      if (!this.report) return;
      await this.persistNow();
      this.report = await this.svc.calculate(this.scopedCtx(this.report.clientId), this.report.id);
      this.saveNotice = `Cálculo API 570 concluído para ${this.report.components.length} ponto(s).`;
      this.noticeKind = "ok";
      this.validationErrors = null;
      this.activeSection = "06";
    }, "Erro ao calcular.");
  }

  private onValidate() {
    return this.run(async () => {
      if (!this.report) return;
      await this.persistNow();
      const result = await this.svc.validate(this.scopedCtx(this.report.clientId), this.report.id);
      this.report = (await this.svc.getReport(this.scopedCtx(this.report.clientId), this.report.id)) || this.report;
      this.validationOk = result.valid;
      this.validationErrors = result.valid ? [] : result.errors;
      this.saveNotice = null;
    }, "Erro ao validar.");
  }

  /** "Visualizar Relatório" e "Gerar PDF": o MESMO snapshot — o Preview é o PDF renderizado. */
  private onPreviewOrPdf(generate: boolean) {
    return this.run(async () => {
      if (!this.report) return;
      await this.persistNow();
      const ctx = this.scopedCtx(this.report.clientId);
      this.previewSnapshot = await this.svc.buildDocumentSnapshot(ctx, this.report.id);
      if (generate) {
        await this.svc.recordPdfGenerated(ctx, this.report.id);
        this.report = (await this.svc.getReport(ctx, this.report.id)) || this.report;
      }
      this.previewOpen = true;
    }, "Erro ao gerar o relatório.");
  }

  private onSignAsPlh() {
    return this.run(async () => {
      if (!this.report) return;
      await this.persistNow();
      this.report = await this.svc.signAsPlh(this.scopedCtx(this.report.clientId), this.report.id);
      await this.loadSignatureImages();
      this.saveNotice = "AVR assinada como PLH. Fontes, resultados e assinaturas foram congelados nesta revisão.";
      this.noticeKind = "ok";
    }, "Erro ao assinar como PLH.");
  }

  /**
   * ENVIAR PARA ANÁLISE DO PLH — passo 1: validação completa (item 5–7).
   * Só com tudo em ordem o modal de confirmação abre; a lista de PLHs vem do
   * gate real, e ninguém é pré-selecionado.
   */
  private onSubmitForPlhClick() {
    return this.run(async () => {
      if (!this.report) return;
      await this.persistNow();
      const result = await this.svc.validateForSubmission(this.scopedCtx(this.report.clientId), this.report.id);
      if (!result.valid) {
        this.validationOk = false;
        this.validationErrors = result.errors;
        this.saveNotice = "A AVR não pode ser enviada para análise do PLH com pendências.";
        this.noticeKind = "error";
        this.activeSection = "02";
        return;
      }
      // PLH RESPONSÁVEL vem do cadastro do CLIENTE — sem seleção manual.
      const resolved = await this.svc.resolvePlhFor(this.scopedCtx(this.report.clientId), this.report.id);
      this.validationErrors = null;
      this.submitModal = { open: true, plhProfessionalId: resolved.plh?.professionalId || "", candidates: [], sending: false, resolved };
    }, "Erro ao preparar o envio para análise do PLH.");
  }

  /** Passo 2: confirmação com o PLH responsável escolhido pelo Engenheiro. */
  private onConfirmSubmitForPlh() {
    return this.run(async () => {
      if (!this.report) return;
      if (!this.submitModal.resolved?.plh) {
        this.saveNotice = this.submitModal.resolved?.reason || CLIENT_WITHOUT_PLH;
        this.noticeKind = "error";
        return;
      }
      this.submitModal.sending = true;
      this.report = await this.svc.submitForPlhReview(this.scopedCtx(this.report.clientId), this.report.id, {});
      await this.loadSignatureImages();
      this.submitModal = { open: false, plhProfessionalId: "", candidates: [], sending: false, resolved: null };
      this.saveNotice = `AVR enviada para análise do PLH ${this.report.plhReview?.plhName || ""}. Status: ${AVR_STATUS_LABELS[this.report.status]}.`;
      this.noticeKind = "ok";
      this.validationErrors = null;
    }, "Erro ao enviar para análise do PLH.").finally(() => { this.submitModal.sending = false; });
  }

  private openPointHistory(key: string) {
    if (!this.report) return;
    const existing = this.report.pointHistories.find(h => h.key === key);
    const term = (ref: { thicknessMm: number; date: string; sourceReference: string } | null | undefined) => ({ thickness: ref ? formatThicknessInput(ref.thicknessMm) : "", date: ref?.date || "", ref: ref?.sourceReference || "" });
    this.historyModal = { open: true, key, short: term(existing?.shortTerm), long: term(existing?.longTerm), errors: [], search: null };
    this.render();
  }

  /** "UTILIZAR" um relatório anterior correlacionado para um dos blocos — origem RELATÓRIO HISTÓRICO com a fonte. */
  private useCandidate(term: "SHORT" | "LONG", index: number) {
    if (!this.report) return;
    const candidate = this.svc.suggestionFor(this.report, this.historyModal.key).candidates[index];
    if (!candidate) return;
    const value = { thickness: formatThicknessInput(candidate.thicknessMm), date: candidate.date, ref: `${candidate.reportNumber} / ${candidate.pointId}` };
    this.historyModal = { ...this.historyModal, [term === "SHORT" ? "short" : "long"]: value, errors: [], search: null };
    this.render();
  }

  private clearTerm(term: "SHORT" | "LONG") {
    this.historyModal = { ...this.historyModal, [term === "SHORT" ? "short" : "long"]: { thickness: "", date: "", ref: "" }, errors: [] };
    this.render();
  }

  private onSavePointHistory() {
    return this.run(async () => {
      if (!this.report) return;
      const m = this.historyModal;
      const readings = this.report.readings.filter(x => correlationKey(x) === m.key).sort((a, b) => a.date.localeCompare(b.date));
      const current = readings[readings.length - 1];
      const filled = (t: { thickness: string; date: string }) => t.thickness.trim() !== "" || t.date.trim() !== "";
      const candidates = this.svc.suggestionFor(this.report, m.key).candidates;
      // A origem é RELATÓRIO HISTÓRICO só quando o valor coincide com o relatório escolhido; qualquer edição vira MANUAL.
      const termInput = (t: { thickness: string; date: string; ref: string }) => {
        if (!filled(t)) return null;
        const match = candidates.find((c: any) => `${c.reportNumber} / ${c.pointId}` === t.ref && formatThicknessInput(c.thicknessMm) === formatThicknessInput(t.thickness) && c.date === t.date);
        return { thicknessMm: t.thickness, date: t.date, origin: match ? "SOURCE_REPORT" as const : "MANUAL" as const, sourceReference: match ? t.ref : "" };
      };
      const input = { shortTerm: termInput(m.short), longTerm: termInput(m.long) };
      const errors = validatePointHistoryInput(input, current?.date || "");
      if (errors.length > 0) { this.historyModal = { ...m, errors }; return; }
      this.report = await this.svc.setPointHistory(this.scopedCtx(this.report.clientId), this.report.id, m.key, input);
      this.historyModal = { open: false, key: "", short: { thickness: "", date: "", ref: "" }, long: { thickness: "", date: "", ref: "" }, errors: [], search: null };
      this.saveNotice = "Histórico do ponto salvo." + (this.report.calculationOutdated ? " O cálculo ficou DESATUALIZADO: execute RECALCULAR." : "");
      this.noticeKind = this.report.calculationOutdated ? "info" : "ok";
    }, "Erro ao salvar o histórico do ponto.");
  }

  private onDeleteAvr() {
    return this.run(async () => {
      if (!this.report) return;
      const number = this.report.avrNumber;
      await this.svc.deleteReport(this.scopedCtx(this.report.clientId), this.report.id);
      this.deleteModal = false;
      this.report = null;
      this.removeAttribute("id");
      window.history.replaceState(null, "", "/integrity/avr");
      this.selection = { clientId: "", projectId: "", unitAreaId: "", equipmentId: "", inspectionId: "" };
      this.projects = []; this.unitAreas = []; this.equipments = []; this.existingReports = [];
      this.saveNotice = null;
      await this.loadClients();
      this.contextError = `AVR ${number} excluída.`;
    }, "Erro ao excluir a AVR.");
  }

  private onToggleRecommendation(id: string, checked: boolean) {
    return this.run(async () => {
      if (!this.report) return;
      const ids = new Set(this.report.recommendationIds);
      if (checked) ids.add(id); else ids.delete(id);
      this.report = await this.svc.linkRecommendations(this.scopedCtx(this.report.clientId), this.report.id, [...ids]);
    }, "Erro ao vincular a Recommendation.");
  }

  private onCroquiFromSource(key: string) {
    return this.run(async () => {
      if (!this.report || !key) return;
      const [kind, reportId] = key.split("|");
      this.report = await this.svc.useCroquiFromSource(this.scopedCtx(this.report.clientId), this.report.id, { kind: kind as any, reportId: reportId || "" });
      this.saveNotice = `Croqui reaproveitado do relatório ${this.report.croqui?.sourceReportNumber || ""}.`;
      this.noticeKind = "ok";
    }, "Erro ao reaproveitar o croqui.");
  }

  private async onCroquiFile(file: File) {
    if (!this.report) return;
    if (!file.type.startsWith("image/")) return this.notify(`"${file.name}" não é uma imagem. Anexe PNG, JPG ou WEBP.`, "error");
    if (file.size > MAX_IMAGE_BYTES) return this.notify(`"${file.name}" excede o limite de 4 MB por imagem.`, "error");
    const image = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
    this.report.croqui = { image, fileName: file.name, sourceReportNumber: null };
    this.scheduleAutosave();
    this.render();
  }

  private notify(message: string, kind: "info" | "ok" | "error" = "info") {
    this.saveNotice = message;
    this.noticeKind = kind;
    this.render();
  }

  private messageOf(e: any, fallback: string): string {
    return e instanceof AppError ? ((e as any).safeMessage || e.message) : (e?.message || fallback);
  }

  // -------------------------------------------------------------- RENDER

  render() {
    const has = (p: string) => contextStore.hasPermission(p);

    if (this.isLoading) {
      this.innerHTML = `
      ${this.ctxLoad.banner()}${hydrationBanner(this.hydration)}<style>${this.styles()}</style>
      <style>${CONTEXT_LOAD_STYLES}${CONTEXT_HYDRATION_STYLES}</style><div class="avr-loading"><span>Carregando AVR...</span></div>`;
      return;
    }
    if (this.loadError) {
      this.innerHTML = `<style>${this.styles()}</style>
        <div class="avr-empty"><h2>AVR — AVALIAÇÃO DE VIDA REMANESCENTE</h2><p class="err">${this.esc(this.loadError)}</p>
        <div style="display:flex; gap:10px;"><button class="btn" id="btn-retry">Tentar novamente</button><button class="btn" id="btn-back">Voltar</button></div></div>`;
      this.querySelector("#btn-retry")?.addEventListener("click", () => { this.loadError = null; this.isLoading = true; this.init(); });
      this.querySelector("#btn-back")?.addEventListener("click", () => router.navigate("/integrity/avr"));
      return;
    }
    if (!this.report) {
      this.renderContextSelection();
      return;
    }

    const r = this.report;
    const wf = avrWorkflowActions(r.status, has);
    const canEdit = wf.canEdit;
    const canSources = wf.canSources;
    const canCalc = wf.canCalculate;
    const canValidate = wf.canValidate;
    const canPdf = wf.canGeneratePdf;
    const canSign = wf.canSign;
    const ro = !canEdit;
    const critical = findCriticalPoint(r.components);
    const correction = r.plhReview?.correction;

    this.innerHTML = `
      <style>${this.styles()}</style>
      <div class="avr-header">
        <div>
          <h1>AVR — Avaliação de Vida Remanescente</h1>
          <p>Taxa de corrosão e vida remanescente pelo motor API 570, a partir de Relatórios ME / B-Scan.</p>
        </div>
        <div class="avr-actions">
          ${canEdit ? `<button class="btn" id="btn-save-draft" ${this.isSaving ? "disabled" : ""}><i data-lucide="save"></i> Salvar Rascunho</button>` : ""}
          ${canSources && r.selectedSources.length > 0 ? `<button class="btn" id="btn-refresh-sources"><i data-lucide="refresh-cw"></i> Atualizar Dados das Fontes</button>` : ""}
          ${canCalc ? `<button class="btn ${r.calculationOutdated ? "warn" : ""}" id="btn-calculate" ${r.selectedSources.length === 0 ? "disabled" : ""}><i data-lucide="calculator"></i> ${r.calculatedAt ? "Recalcular" : "Calcular"}</button>` : ""}
          ${canValidate ? `<button class="btn" id="btn-validate"><i data-lucide="check-circle"></i> Validar</button>` : ""}
          <button class="btn" id="btn-preview"><i data-lucide="eye"></i> Visualizar Relatório</button>
          ${canPdf ? `<button class="btn primary" id="btn-generate-pdf"><i data-lucide="file-down"></i> Gerar PDF</button>` : ""}
          ${wf.showSubmit ? `<button class="btn workflow" id="btn-submit-plh" ${this.isWorking ? "disabled" : ""}><i data-lucide="send"></i> ${this.esc(wf.submitLabel)}</button>` : ""}
          <button class="btn" id="btn-history"><i data-lucide="history"></i> Histórico</button>
          ${this.svc.canDelete(r) && has(PermissionCode.AvrUpdate) ? `<button class="btn danger" id="btn-delete-avr"><i data-lucide="trash-2"></i> Excluir AVR</button>` : ""}
        </div>
      </div>

      <div class="avr-meta">
        <div class="meta"><span class="lbl">Nº AVR</span><span class="val">${this.esc(r.avrNumber)}</span></div>
        <div class="meta"><span class="lbl">Revisão</span><span class="val">${this.esc(r.revision)}</span></div>
        <div class="meta"><span class="lbl">Status</span><span class="status ${statusBadgeClass(r.status)}">${r.status === "OUTDATED" ? "CÁLCULO DESATUALIZADO" : this.esc(AVR_STATUS_LABELS[r.status])}</span>${r.plhReview && wf.locked && r.status !== "SIGNED" ? `<span class="hint">PLH: ${this.esc(r.plhReview.plhName)}</span>` : ""}</div>
        <div class="meta"><span class="lbl">Equipamento / TAG</span><span class="val">${this.esc(r.equipmentTag || "—")}</span></div>
        <div class="meta"><span class="lbl">Ponto crítico</span><span class="val">${critical ? `${this.esc(critical.pointId)} · VR ${formatNumberBR(critical.api570?.remainingLifeYears, 1)} anos` : "—"}</span></div>
      </div>

      ${this.saveNotice ? `<div class="avr-notice avr-notice-${this.noticeKind}">${this.esc(this.saveNotice)}</div>` : ""}
      ${this.renderPlhAnalysisPanel(r)}
      ${aiReviewSlot("AVR", r.id, `AVR ${r.avrNumber || ""}`)}
      ${wf.locked && r.status !== "SIGNED" ? `<div class="avr-notice"><strong>${this.esc(AVR_STATUS_LABELS[r.status].toUpperCase())}</strong> — PLH responsável: ${this.esc(r.plhReview?.plhName || "—")} · enviada em ${this.esc(r.plhReview?.submittedAt ? new Date(r.plhReview.submittedAt).toLocaleString("pt-BR") : "—")} por ${this.esc(r.plhReview?.submittedByName || "—")}. Os dados técnicos estão bloqueados: alterações só pelo fluxo de correção.${r.status === "PLH_APPROVED" ? " Análise aprovada — a AVR pode ser assinada pelo PLH." : ""}</div>` : ""}
      ${wf.inCorrection && correction ? `<div class="avr-notice avr-notice-correction"><strong>CORREÇÃO SOLICITADA PELO PLH</strong> — ${this.esc(correction.requestedByName)} em ${this.esc(new Date(correction.requestedAt).toLocaleString("pt-BR"))}<br><strong>Motivo / orientação:</strong> ${this.esc(correction.reason)}${correction.points ? `<br><strong>Pontos / campos:</strong> ${this.esc(correction.points)}` : ""}<br><span class="hint">Corrija, recalcule se algum dado de cálculo mudou e use "Enviar Novamente para Análise do PLH".</span></div>` : ""}
      ${r.calculationOutdated ? `<div class="avr-notice avr-notice-error"><strong>CÁLCULO DESATUALIZADO</strong> — as fontes ou os dados de entrada mudaram depois do último cálculo. Execute RECALCULAR antes de validar, enviar ao PLH ou assinar.</div>` : ""}
      ${this.validationErrors !== null ? this.renderValidationBanner() : ""}

      <div class="avr-layout">
        <nav class="avr-nav">
          ${SECTIONS.map(([num, label]) => `<a class="avr-nav-item ${this.activeSection === num ? "active" : ""}" data-sec="${num}" href="#avr-sec-${num}"><span class="num">${num}</span><span>${label}</span></a>`).join("")}
        </nav>
        <div class="avr-main">
          ${this.renderCardContext(r, ro)}
          ${this.renderCardSources(r, canSources)}
          ${this.renderCardImported(r, ro)}
          ${this.renderCardComplementary(r, ro)}
          ${this.renderCardRate(r, ro)}
          ${this.renderCardLife(r)}
          ${this.renderCardAnalysis(r, ro)}
          ${this.renderCardRecommendations(r, ro)}
          ${this.renderCardCroqui(r, ro)}
          ${this.renderCardSignatories(r, ro, canSign)}
        </div>
      </div>

      ${this.previewOpen ? this.renderPreviewModal(r) : ""}
      ${this.historyOpen ? this.renderHistoryModal(r) : ""}
      ${this.submitModal.open ? this.renderSubmitModal(r) : ""}
      ${this.historyModal.open ? this.renderHistoryPointModal(r) : ""}
      ${this.deleteModal ? this.renderDeleteModal(r) : ""}
      ${this.lightbox.open ? this.renderLightbox() : ""}
    `;

    createIcons({ icons: { Save, Eye, CheckCircle, FileDown, History, RefreshCw, Calculator, PenLine, Upload, Trash2, ExternalLink, Send, ClipboardList, Image: ImageIcon }, nameAttr: "data-lucide", root: this });
    if (this.previewOpen && this.previewSnapshot) {
      const preview = this.querySelector("#avr-pdf-preview") as any;
      if (preview) preview.snapshot = this.previewSnapshot;
    }
    this.attachEvents(ro, canSources);
  }

  private opt(value: string, label: string, selected: boolean) {
    return `<option value="${this.esc(value)}" ${selected ? "selected" : ""}>${this.esc(label)}</option>`;
  }

  private dis(ro: boolean) { return ro ? "disabled" : ""; }

  private renderContextSelection() {
    const sel = this.selection;
    const ready = canCreateAvrFromContext(sel);
    const canCreate = contextStore.hasPermission(PermissionCode.AvrCreate);
    this.innerHTML = `
      <style>${this.styles()}</style>
      <div class="avr-header"><div><h1>AVR — Avaliação de Vida Remanescente</h1><p>Selecione o contexto: Cliente → Projeto → Unidade/Área → Equipamento.</p></div></div>
      <section class="avr-card">
        <div class="card-head"><span class="card-title">Contexto da AVR</span></div>
        <div class="grid cols-4">
          <label class="field"><span>Cliente *</span>
            <select class="ctl" id="sel-ctx-client">
              ${this.opt("", this.clients.length === 0 ? "Nenhum cliente autorizado" : "Selecione o Cliente...", !sel.clientId)}
              ${this.clients.map((c: any) => this.opt(c.id, c.name || c.legalName, sel.clientId === c.id)).join("")}
            </select></label>
          <label class="field"><span>Projeto *</span>
            <select class="ctl" id="sel-ctx-project" ${!sel.clientId ? "disabled" : ""}>
              ${this.opt("", !sel.clientId ? "Selecione o Cliente primeiro" : this.projects.length === 0 ? "Nenhum Projeto para este Cliente" : "Selecione o Projeto...", !sel.projectId)}
              ${this.projects.map((p: any) => this.opt(p.id, `${p.code} — ${p.name}`, sel.projectId === p.id)).join("")}
            </select></label>
          <label class="field"><span>Unidade / Área *</span>
            <select class="ctl" id="sel-ctx-unit-area" ${!sel.projectId ? "disabled" : ""}>
              ${this.opt("", !sel.projectId ? "Selecione o Projeto primeiro" : this.unitAreas.length === 0 ? "Nenhuma Unidade/Área neste Projeto" : "Selecione a Unidade/Área...", !sel.unitAreaId)}
              ${this.unitAreas.map((u: any) => this.opt(u.id, `${u.code} — ${u.name}`, sel.unitAreaId === u.id)).join("")}
            </select></label>
          <label class="field"><span>Equipamento / TAG *</span>
            <select class="ctl" id="sel-ctx-equipment" ${!sel.clientId ? "disabled" : ""}>
              ${this.opt("", !sel.clientId ? "Selecione o Cliente primeiro" : this.equipments.length === 0 ? "Nenhum equipamento neste contexto" : "Selecione o Equipamento...", !sel.equipmentId)}
              ${this.equipments.map((e: any) => this.opt(e.id, `${e.tag} — ${e.name || ""}`, sel.equipmentId === e.id)).join("")}
            </select></label>
        </div>
        ${this.contextError ? `<div class="avr-notice avr-notice-error" style="margin-top:14px;">${this.esc(this.contextError)}</div>` : ""}
        ${sel.equipmentId && this.existingReports.length > 0 ? `
          <div style="margin-top:14px;">
            <p class="hint" style="margin:0 0 6px 0;">AVRs existentes para este equipamento:</p>
            <table class="avr-table"><thead><tr><th>Nº AVR</th><th>Rev.</th><th>Status</th><th>Fontes</th><th>Atualizada em</th><th></th></tr></thead>
            <tbody>${this.existingReports.map(a => `<tr><td>${this.esc(a.avrNumber)}</td><td>${this.esc(a.revision)}</td><td><span class="status ${statusBadgeClass(a.status)}">${this.esc(AVR_STATUS_LABELS[a.status])}</span></td><td>${this.esc(a.selectedSources.map(s => s.reportNumber).join(", ") || "—")}</td><td>${this.esc(new Date(a.updatedAt).toLocaleString("pt-BR"))}</td><td><button class="btn sm" data-open-avr="${this.esc(a.id)}">Abrir</button></td></tr>`).join("")}</tbody></table>
          </div>` : ""}
        <div class="avr-actions" style="margin-top:16px; justify-content:flex-start;">
          ${canCreate
            ? `<button class="btn primary" id="btn-create-avr" ${this.isResolvingContext || !ready ? "disabled" : ""}>${this.isResolvingContext ? "Criando..." : "Criar AVR"}</button>`
            : `<span class="hint">Você não possui permissão para criar AVR.</span>`}
        </div>
      </section>`;
    this.querySelector("#sel-ctx-client")?.addEventListener("change", e => this.onContextChange("clientId", (e.target as HTMLSelectElement).value));
    this.querySelector("#sel-ctx-project")?.addEventListener("change", e => this.onContextChange("projectId", (e.target as HTMLSelectElement).value));
    this.querySelector("#sel-ctx-unit-area")?.addEventListener("change", e => this.onContextChange("unitAreaId", (e.target as HTMLSelectElement).value));
    this.querySelector("#sel-ctx-equipment")?.addEventListener("change", e => this.onContextChange("equipmentId", (e.target as HTMLSelectElement).value));
    this.querySelector("#btn-create-avr")?.addEventListener("click", () => this.onCreateFromContext());
    this.querySelectorAll("[data-open-avr]").forEach(btn => btn.addEventListener("click", () => this.onOpenExisting((btn as HTMLElement).dataset["openAvr"]!)));
  }

  /**
   * CARD ANÁLISE DO PLH no próprio Relatório AVR.
   *
   * As quatro ações (iniciar, aprovar, devolver, reprovar) existiam — mas só
   * na tela separada `/engineering/avr/:id`. Abrindo a AVR, o PLH não tinha o
   * que clicar e concluía que o fluxo não estava implementado.
   *
   * O botão leva à tela de análise em vez de repetir as ações aqui: aprovar e
   * solicitar correção exigem parecer e pontos/campos, formulários que já
   * existem lá. Duplicá-los criaria um caminho mais fraco do que o servidor
   * aceita — o mesmo motivo pelo qual a devolução do ME leva à análise.
   */
  private renderPlhAnalysisPanel(r: AvrReport): string {
    const review = r.plhReview;
    const inCycle = AVR_PLH_QUEUE_STATUSES.includes(r.status as never) || r.status === "PLH_APPROVED";
    if (!review && !inCycle) return "";

    const me = contextStore.get().professionalId || null;
    const assigned = !!me && String(review?.plhProfessionalId ?? "") === String(me);
    const when = (iso?: string | null) => (iso ? new Date(iso).toLocaleString("pt-BR") : "—");

    const canAct = assigned && AVR_PLH_QUEUE_STATUSES.includes(r.status as never);
    const reason = !review
      ? "Esta AVR ainda não foi enviada para análise do PLH."
      : !me
        ? "Sua conta não possui um Profissional vinculado. Peça à Administração para vincular em Pessoas e Acessos."
        : !assigned
          ? `A análise está atribuída a ${review.plhName || "outro Engenheiro PLH"}. Somente o PLH responsável pelo Cliente desta AVR pode analisá-la.`
          : r.status === "PLH_APPROVED"
            ? "Análise já aprovada. Para alterar o conteúdo, crie uma nova revisão."
            : "";

    return `
      <section class="plh-panel">
        <div class="plh-panel-head">ANÁLISE DO PLH</div>
        <div class="plh-panel-grid">
          <span><b>Status:</b> ${this.esc(AVR_STATUS_LABELS[r.status])}</span>
          <span><b>PLH responsável:</b> ${this.esc(review?.plhName || "não definido")}</span>
          <span><b>Enviada em:</b> ${this.esc(when(review?.submittedAt))}</span>
          ${review?.approvedAt ? `<span><b>Aprovada em:</b> ${this.esc(when(review.approvedAt))}</span>` : ""}
        </div>
        ${canAct
          ? `<div class="plh-panel-actions">
               <button class="btn-action primary plh-start" data-open-avr-review="${this.esc(r.id)}">
                 <i data-lucide="clipboard-check"></i> ${r.status === "PLH_REVIEWING" ? "CONTINUAR ANÁLISE" : "INICIAR ANÁLISE"}
               </button>
             </div>
             <p class="plh-panel-reason">Na tela de análise: aprovar com parecer, solicitar correção por ponto/campo ou reprovar com motivo.</p>`
          : `<p class="plh-panel-reason">${this.esc(reason)}</p>`}
      </section>`;
  }

  private renderValidationBanner(): string {
    if (this.validationOk) return `<div class="avr-notice avr-notice-ok">AVR validada — completa e com cálculo atualizado.</div>`;
    const errors = this.validationErrors || [];
    return `<div class="avr-notice avr-notice-error"><strong>Pendências encontradas:</strong><ul>${errors.map(e => `<li>${this.esc(e)}</li>`).join("")}</ul></div>`;
  }

  // ------------------------------------------------------------- CARDS

  private renderCardContext(r: AvrReport, ro: boolean): string {
    return `
      <section class="avr-card" id="avr-sec-01">
        <div class="card-head"><span class="card-title"><span class="card-num">01.</span> Contexto</span><button class="btn sm" id="btn-change-context">Trocar Contexto</button></div>
        <div class="grid cols-4">
          <label class="field"><span>Cliente</span><input class="ctl" readonly value="${this.esc(r.clientDisplayName || "—")}"></label>
          <label class="field"><span>Projeto / Contrato</span><input class="ctl" readonly value="${this.esc(r.projectContract || "—")}"></label>
          <label class="field"><span>Unidade / Área</span><input class="ctl" readonly value="${this.esc(r.unitArea || "—")}"></label>
          <label class="field"><span>Equipamento / TAG</span><input class="ctl" readonly value="${this.esc(r.equipmentTag || "—")}"></label>
        </div>
        <div class="grid cols-4" style="margin-top:12px;">
          <label class="field"><span>Tipo de Equipamento</span><input class="ctl" readonly value="${this.esc(r.equipmentTypeLabel || "—")}"></label>
          <label class="field"><span>Material</span><input class="ctl" data-field="material" value="${this.esc(r.material)}" ${this.dis(ro)}></label>
          <label class="field"><span>Fluido / Serviço</span><input class="ctl" data-field="fluid" value="${this.esc(r.fluid)}" ${this.dis(ro)}></label>
          <label class="field"><span>Rel. cliente nº</span><input class="ctl" data-field="clientReportNumber" value="${this.esc(r.clientReportNumber)}" ${this.dis(ro)}></label>
        </div>
        <div class="grid cols-4" style="margin-top:12px;">
          <label class="field"><span>Ordem de Serviço</span>
            <select class="ctl" id="sel-work-order" ${this.dis(ro)}>
              ${this.opt("", this.workOrders.length === 0 ? "Nenhuma OS para este contexto" : "Selecione a OS...", !r.osWorkOrderId)}
              ${this.workOrders.map((w: any) => this.opt(w.id, `${w.code}${w.description ? " — " + w.description : ""}`, r.osWorkOrderId === w.id)).join("")}
            </select></label>
          <label class="field"><span>Sub-OS</span>
            <select class="ctl" id="sel-sub-work-order" ${this.dis(ro) || (!r.osWorkOrderId ? "disabled" : "")}>
              ${this.opt("", !r.osWorkOrderId ? "Selecione a OS primeiro" : this.subWorkOrders.length === 0 ? "Nenhuma Sub-OS nesta OS" : "Selecione a Sub-OS...", !r.osSubWorkOrderId)}
              ${this.subWorkOrders.map((s: any) => this.opt(s.id, `${s.code}${s.description ? " — " + s.description : ""}`, r.osSubWorkOrderId === s.id)).join("")}
            </select></label>
          <label class="field"><span>Data de emissão</span><input class="ctl" readonly value="${this.esc(formatCivilDate(r.issueDate.slice(0, 10)))}"></label>
          <label class="field"><span>Criada por</span><input class="ctl" readonly value="${this.esc(r.createdBy)}"></label>
        </div>
      </section>`;
  }

  private renderCardSources(r: AvrReport, canSources: boolean): string {
    const filtered = applyAvrSourceFilters(this.availableSources, this.sourceFilters);
    const selectedKeys = new Set(r.selectedSources.map(s => this.sourceKey(s)));
    const dirty = filtered.some(s => this.pendingSources.has(this.sourceKey(s)) !== selectedKeys.has(this.sourceKey(s))) || this.pendingSources.size !== selectedKeys.size;
    return `
      <section class="avr-card" id="avr-sec-02">
        <div class="card-head"><span class="card-title"><span class="card-num">02.</span> Fontes de Dados <span class="count">${r.selectedSources.length} selecionada(s)</span></span></div>
        <p class="hint" style="margin:0 0 10px 0;">Somente Relatórios ME e B-Scan deste equipamento podem alimentar a AVR. Fontes VALIDADAS/ASSINADAS são as preferidas; uma fonte não aprovada é sinalizada e impede a validação da AVR.</p>
        <div class="grid cols-4" style="margin-bottom:10px;">
          <label class="field"><span>Tipo</span>
            <select class="ctl sm" id="flt-kind">
              ${this.opt("ALL", "Todos (ME + B-Scan)", this.sourceFilters.kind === "ALL")}
              ${this.opt("ME", "ME — Medição de Espessura", this.sourceFilters.kind === "ME")}
              ${this.opt("BSCAN", "B-Scan", this.sourceFilters.kind === "BSCAN")}
            </select></label>
          <label class="field"><span>Período — de</span><input class="ctl sm" type="date" id="flt-from" value="${this.esc(this.sourceFilters.from)}"></label>
          <label class="field"><span>Período — até</span><input class="ctl sm" type="date" id="flt-to" value="${this.esc(this.sourceFilters.to)}"></label>
          <div class="field" style="justify-content:flex-end;">${canSources ? `<button class="btn primary sm" id="btn-apply-sources" ${dirty ? "" : "disabled"}>Aplicar Fontes Selecionadas</button>` : ""}</div>
        </div>
        <table class="avr-table">
          <thead><tr><th></th><th>Tipo</th><th>Relatório Nº</th><th>Rev.</th><th>Data do Ensaio</th><th>Status</th><th>Situação</th><th>OS</th></tr></thead>
          <tbody>
            ${filtered.length === 0
              ? `<tr><td class="empty" colspan="8">${this.availableSources.length === 0 ? "Nenhum Relatório ME ou B-Scan encontrado para este equipamento." : "Nenhum relatório no filtro."}</td></tr>`
              : filtered.map(s => {
                const key = this.sourceKey(s);
                return `<tr>
                  <td><input type="checkbox" data-source-key="${this.esc(key)}" ${this.pendingSources.has(key) ? "checked" : ""} ${canSources ? "" : "disabled"}></td>
                  <td>${this.esc(AVR_SOURCE_KIND_LABELS[s.kind])}</td>
                  <td><strong>${this.esc(s.reportNumber)}</strong></td>
                  <td>${this.esc(s.revision)}</td>
                  <td>${this.esc(formatCivilDate(s.testDate))}</td>
                  <td>${this.esc(s.status)}</td>
                  <td>${s.approved ? `<span class="tag ok">APROVADA</span>` : `<span class="tag bad">FONTE NÃO APROVADA</span>`}</td>
                  <td>${this.esc(s.osNumber || "—")}</td>
                </tr>`;
              }).join("")}
          </tbody>
        </table>
        ${this.availableSources.some(s => s.kind === "BSCAN") ? "" : `<p class="hint" style="margin:8px 0 0 0;">B-Scan: nenhum Relatório B-Scan gravado para este equipamento.</p>`}
      </section>`;
  }

  private renderCardImported(r: AvrReport, ro: boolean): string {
    return `
      <section class="avr-card" id="avr-sec-03">
        <div class="card-head"><span class="card-title"><span class="card-num">03.</span> Dados Importados <span class="count">${r.readings.length} leitura(s)</span></span></div>
        <p class="hint" style="margin:0 0 10px 0;">Somente leitura: cada espessura carrega o relatório, a revisão, a data e o ponto de origem. Para alterar, altere o relatório de origem e use "Atualizar Dados das Fontes".</p>
        <table class="avr-table">
          <thead><tr><th>Origem</th><th>Relatório</th><th>Rev.</th><th>Data</th><th>Ponto</th><th>Componente</th><th>Localização</th><th>E. Nominal</th><th>E. Mínima</th><th>E. Medida</th><th>Histórico</th><th>Ação</th></tr></thead>
          <tbody>
            ${r.readings.length === 0
              ? `<tr><td class="empty" colspan="12">Nenhuma leitura importada — selecione as fontes na seção 02.</td></tr>`
              : r.readings.map(x => {
                const key = correlationKey(x);
                const latest = this.latestReadingOf(r, key);
                const isCurrent = latest === x;
                const hist = this.svc.historyStateFor(r, key);
                const editable = !ro && isCurrent;
                const stateTag = hist.state === "COMPLETO" ? `<span class="tag ok">COMPLETO</span>` : hist.state === "PARCIAL" ? `<span class="tag warn">PARCIAL</span>` : `<span class="tag bad">PENDENTE</span>`;
                const line = (label: string, ref: { thicknessMm: number; date: string; origin: string; reference: string } | null) => ref ? `<small title="${this.esc(ref.reference)}">${label}: ${formatNumberBR(ref.thicknessMm)} | ${this.esc(formatCivilDate(ref.date))}${ref.origin === "MANUAL" ? " ✎" : ""}</small>` : `<small class="hint">${label}: —</small>`;
                return `<tr>
                  <td>${this.esc(AVR_SOURCE_KIND_LABELS[x.sourceKind])}${x.approved ? "" : ` <span class="tag bad">NÃO APROVADA</span>`}</td>
                  <td>${this.esc(x.reportNumber)}</td><td>${this.esc(x.revision)}</td><td>${this.esc(formatCivilDate(x.date))}</td>
                  <td><strong>${this.esc(x.pointId)}</strong></td><td>${this.esc(x.component || "—")}</td><td>${this.esc(x.location || "—")}</td>
                  <td>${formatNumberBR(x.nominalMm)}</td><td>${formatNumberBR(x.minMm)}</td><td><strong>${formatNumberBR(x.thicknessMm)}</strong></td>
                  <td>${isCurrent ? `${stateTag}<br>${line("CURTO", hist.shortTerm)}<br>${line("LONGO", hist.longTerm)}` : `<small>leitura anterior</small>`}</td>
                  <td>${editable ? `<button class="btn sm" data-point-history="${this.esc(key)}"><i data-lucide="clipboard-list"></i> ${hist.manual ? "Editar Histórico" : "Informar Histórico"}</button>` : ""}</td>
                </tr>`;
              }).join("")}
          </tbody>
        </table>
        <p class="hint" style="margin-top:8px;">Espessura Atual e Data Atual vêm do ME/B-Scan (somente leitura). Cada ponto tem duas referências históricas independentes — CURTO PRAZO e LONGO PRAZO — informadas aqui (origem "${AVR_POINT_HISTORY_ORIGIN_LABELS.MANUAL}", marcadas ✎) ou selecionadas de um relatório anterior correlacionado (origem "${AVR_POINT_HISTORY_ORIGIN_LABELS.SOURCE_REPORT}"). Estado: COMPLETO (curto + longo), PARCIAL, PENDENTE.</p>
      </section>`;
  }

  private latestReadingOf(r: AvrReport, key: string) {
    return r.readings.filter(x => correlationKey(x) === key).sort((a, b) => a.date.localeCompare(b.date)).pop() ?? null;
  }

  /** HISTÓRICO DO PONTO Pxx — medição atual em leitura + dois blocos: CURTO PRAZO e LONGO PRAZO. */
  private renderHistoryPointModal(r: AvrReport): string {
    const m = this.historyModal;
    const current = this.latestReadingOf(r, m.key);
    if (!current) return "";
    const suggestion = this.svc.suggestionFor(r, m.key);
    const info = (label: string, value: string) => `<div class="meta"><span class="lbl">${label}</span><span class="val" style="font-size:0.84rem;">${this.esc(value || "—")}</span></div>`;
    const block = (term: "SHORT" | "LONG", title: string, value: { thickness: string; date: string; ref: string }, suggested: { thicknessMm: number; date: string; reference: string } | null) => {
      const id = term === "SHORT" ? "short" : "long";
      const labelTerm = term === "SHORT" ? "de Curto Prazo" : "de Longo Prazo";
      const origin = value.ref ? `${AVR_POINT_HISTORY_ORIGIN_LABELS.SOURCE_REPORT} · ${value.ref}` : (value.thickness || value.date ? AVR_POINT_HISTORY_ORIGIN_LABELS.MANUAL : "—");
      return `
        <div class="avr-card" style="padding:12px; background: var(--ia-bg-panel, #0F131A);">
          <div class="card-head" style="margin-bottom:8px;"><span class="card-title" style="font-size:0.78rem;">${title}</span>
            <div style="display:flex; gap:6px;">
              ${suggestion.candidates.length > 0 ? `<button class="btn sm" data-search-term="${term}">Buscar em relatórios anteriores</button>` : ""}
              ${value.thickness || value.date ? `<button class="btn sm danger" data-clear-term="${term}">Limpar</button>` : ""}
            </div></div>
          ${m.search === term ? `
            <div class="avr-notice" style="margin-bottom:8px;"><strong>RELATÓRIOS ANTERIORES DO PONTO ${this.esc(current.pointId)}</strong> (mesmo TAG · ponto · componente · localização)
              <table class="avr-table" style="margin-top:6px;"><thead><tr><th>Relatório</th><th>Rev.</th><th>Data</th><th>Ponto</th><th>Espessura</th><th></th></tr></thead>
              <tbody>${suggestion.candidates.map((c: any, i: number) => `<tr><td>${this.esc(c.reportNumber)}</td><td>${this.esc(c.revision)}</td><td>${this.esc(formatCivilDate(c.date))}</td><td>${this.esc(c.pointId)}</td><td>${formatNumberBR(c.thicknessMm)} mm</td><td><button class="btn sm primary" data-use-candidate="${term}:${i}">Utilizar</button></td></tr>`).join("")}</tbody></table>
            </div>` : ""}
          ${suggested && !value.thickness ? `<p class="hint" style="margin:0 0 8px 0;">Sugestão: ${formatNumberBR(suggested.thicknessMm)} mm em ${this.esc(formatCivilDate(suggested.date))} · FONTE: ${this.esc(suggested.reference)}</p>` : ""}
          <div class="grid cols-2">
            <label class="field"><span>Espessura ${labelTerm} (mm) *</span><input class="ctl" id="inp-${id}-thickness" inputmode="decimal" placeholder="0,00" value="${this.esc(value.thickness)}"></label>
            <label class="field"><span>Data da Medição ${labelTerm} *</span><input class="ctl" type="date" id="inp-${id}-date" value="${this.esc(value.date)}" max="${this.esc(current.date)}"></label>
          </div>
          <div class="grid cols-2" style="margin-top:8px;">
            <label class="field"><span>Origem</span><input class="ctl" readonly value="${this.esc(origin)}"></label>
            <label class="field"><span>Relatório de origem</span><input class="ctl" readonly value="${this.esc(value.ref || (value.thickness || value.date ? "— (informado no AVR)" : "—"))}"></label>
          </div>
        </div>`;
    };
    return `
      <div class="modal-backdrop" id="avr-history-point-modal">
        <div class="modal-box" style="max-width:880px;">
          <div class="modal-head"><span><i data-lucide="clipboard-list"></i> HISTÓRICO DO PONTO ${this.esc(current.pointId)}</span><button class="icon-btn" id="btn-close-point-history">✕</button></div>
          <div class="modal-body">
            <div class="avr-meta" style="grid-template-columns: repeat(4, 1fr); margin-bottom:12px;">
              ${info("Ponto", current.pointId)}${info("Componente", current.component)}${info("Localização", current.location)}${info("Fonte", `${current.reportNumber} / Rev. ${current.revision}`)}
              ${info("Espessura Atual", `${formatNumberBR(current.thicknessMm)} mm`)}${info("Data Atual", formatCivilDate(current.date))}${info("Espessura Nominal", current.nominalMm === null ? "" : `${formatNumberBR(current.nominalMm)} mm`)}${info("Espessura Mínima", current.minMm === null ? "" : `${formatNumberBR(current.minMm)} mm`)}
            </div>
            <div style="display:flex; flex-direction:column; gap:12px;">
              ${block("SHORT", "HISTÓRICO — CURTO PRAZO", m.short, suggestion.shortTerm)}
              ${block("LONG", "HISTÓRICO — LONGO PRAZO", m.long, suggestion.longTerm)}
            </div>
            ${m.errors.length > 0 ? `<div class="avr-notice avr-notice-error" style="margin-top:10px;"><ul style="margin:0; padding-left:18px;">${m.errors.map(e => `<li>${this.esc(e)}</li>`).join("")}</ul></div>` : ""}
            <p class="hint" style="margin-top:8px;">Formato 0,00 (valor interno numérico). Cronologia obrigatória: LONGO PRAZO &lt; CURTO PRAZO &lt; ATUAL (${this.esc(formatCivilDate(current.date))}). Um bloco vazio fica como não informado — o rascunho pode ser salvo; a validação final exige os dois.</p>
            <div class="avr-actions" style="justify-content:flex-end; margin-top:12px;">
              <button class="btn" id="btn-cancel-point-history">Cancelar</button>
              <button class="btn primary" id="btn-save-point-history"><i data-lucide="save"></i> Salvar Histórico</button>
            </div>
          </div>
        </div>
      </div>`;
  }

  /** EXCLUIR AVR? — só em elaboração; a ação não pode ser desfeita. */
  private renderDeleteModal(r: AvrReport): string {
    return `
      <div class="modal-backdrop" id="avr-delete-modal">
        <div class="modal-box">
          <div class="modal-head"><span><i data-lucide="trash-2"></i> EXCLUIR AVR?</span><button class="icon-btn" id="btn-close-delete">✕</button></div>
          <div class="modal-body">
            <div class="grid cols-3">
              <label class="field"><span>AVR</span><input class="ctl" readonly value="${this.esc(r.avrNumber)} · Rev. ${this.esc(r.revision)}"></label>
              <label class="field"><span>TAG</span><input class="ctl" readonly value="${this.esc(r.equipmentTag || "—")}"></label>
              <label class="field"><span>Status</span><input class="ctl" readonly value="${this.esc(AVR_STATUS_LABELS[r.status])}"></label>
            </div>
            <div class="avr-notice avr-notice-error" style="margin-top:14px;">Deseja realmente excluir este AVR? Esta ação não poderá ser desfeita.</div>
            <div class="avr-actions" style="justify-content:flex-end; margin-top:6px;">
              <button class="btn" id="btn-cancel-delete">Cancelar</button>
              <button class="btn danger" id="btn-confirm-delete"><i data-lucide="trash-2"></i> Excluir AVR</button>
            </div>
          </div>
        </div>
      </div>`;
  }

  private renderCardComplementary(r: AvrReport, ro: boolean): string {
    const c = r.complementary;
    return `
      <section class="avr-card" id="avr-sec-04">
        <div class="card-head"><span class="card-title"><span class="card-num">04.</span> Dados Complementares</span></div>
        <div class="grid cols-4">
          <label class="field"><span>Data da Avaliação *</span><input class="ctl" type="date" data-comp="evaluationDate" value="${this.esc(c.evaluationDate)}" ${this.dis(ro)}></label>
          <label class="field"><span>Norma / Critério</span><input class="ctl" data-comp="criterionNorm" value="${this.esc(c.criterionNorm)}" placeholder="API 570 / API 510 / ASME B31.3" ${this.dis(ro)}></label>
          <label class="field"><span>Espessura mínima adotada (mm)</span><input class="ctl" data-comp="minimumThicknessAdoptedMm" value="${c.minimumThicknessAdoptedMm === null ? "" : this.esc(String(c.minimumThicknessAdoptedMm).replace(".", ","))}" placeholder="usada só nos pontos sem mínima no relatório" ${this.dis(ro)}></label>
          <label class="field"><span>Origem da espessura mínima</span><input class="ctl" data-comp="minimumThicknessOrigin" value="${this.esc(c.minimumThicknessOrigin)}" placeholder="Cálculo ASME B31.3 nº … / Projeto" ${this.dis(ro)}></label>
        </div>
        <div class="grid cols-2" style="margin-top:12px;">
          <label class="field"><span>Premissas</span><textarea class="ctl textarea sm" data-comp="assumptions" ${this.dis(ro)}>${this.esc(c.assumptions)}</textarea></label>
          <label class="field"><span>Observações</span><textarea class="ctl textarea sm" data-comp="observations" ${this.dis(ro)}>${this.esc(c.observations)}</textarea></label>
        </div>
        <div class="grid cols-4" style="margin-top:12px;">
          <label class="field"><span>Intervalo de inspeção proposto</span><input class="ctl" data-comp="proposedInspectionInterval" value="${this.esc(c.proposedInspectionInterval)}" ${this.dis(ro)}></label>
        </div>
        <p class="hint" style="margin:8px 0 0 0;">Alterar a espessura mínima adotada ou a base da taxa marca o cálculo como desatualizado.</p>
      </section>`;
  }

  private renderCardRate(r: AvrReport, ro: boolean): string {
    const calc = r.components.filter(c => c.api570);
    return `
      <section class="avr-card" id="avr-sec-05">
        <div class="card-head"><span class="card-title"><span class="card-num">05.</span> Taxa de Corrosão — API 570</span></div>
        <div class="grid cols-2">
          <label class="field"><span>Base da taxa adotada</span>
            <select class="ctl" data-field="rateBasis" ${this.dis(ro)}>${RATE_BASIS_OPTIONS.map(([v, l]) => this.opt(v, l, r.rateBasis === v)).join("")}</select></label>
          <label class="field"><span>Justificativa (obrigatória quando a base não é a conservadora)</span><input class="ctl" data-field="rateBasisJustification" value="${this.esc(r.rateBasisJustification)}" ${this.dis(ro)}></label>
        </div>
        <p class="hint" style="margin:8px 0 10px 0;">O motor API 570 recebe, por ponto, ATUAL + CURTO PRAZO + LONGO PRAZO (espessura + data) e devolve TC curto prazo, TC longo prazo e a TAXA ADOTADA pela regra dele. Nenhuma taxa é digitada nem escolhida fora do motor.</p>
        <table class="avr-table">
          <thead><tr><th>Ponto</th><th>Atual</th><th>Curto Prazo</th><th>TC Curto Prazo</th><th>Longo Prazo</th><th>TC Longo Prazo</th><th>TC Adotada</th><th>Base</th><th>Situação</th></tr></thead>
          <tbody>
            ${r.components.length === 0
              ? `<tr><td class="empty" colspan="9">${r.selectedSources.length === 0 ? "Selecione as fontes e execute CALCULAR." : "Execute CALCULAR."}</td></tr>`
              : r.components.map(c => {
                const last = c.readings[c.readings.length - 1];
                const refCell = (ref: { thicknessMm: number; date: string; origin: string; reference: string } | null) => ref ? `${formatNumberBR(ref.thicknessMm)} mm<br><small>${this.esc(formatCivilDate(ref.date))} · ${ref.origin === "MANUAL" ? "dado compl. AVR" : this.esc(ref.reference)}</small>` : `<span class="tag warn">DADOS INSUFICIENTES</span>`;
                const rate = (v: number | null | undefined, ref: unknown) => ref ? formatNumberBR(v, 3) : `<small class="hint">—</small>`;
                return `<tr>
                  <td><strong>${this.esc(c.pointId)}</strong>${c.component ? `<br><small>${this.esc(c.component)}</small>` : ""}</td>
                  <td>${last ? `${formatNumberBR(last.thicknessMm)} mm<br><small>${this.esc(formatCivilDate(last.date))} · ${this.esc(last.reportNumber)}</small>` : "—"}</td>
                  <td>${refCell(c.shortTerm)}</td>
                  <td>${rate(c.api570?.shortTermRateMmPerYear, c.shortTerm)}</td>
                  <td>${refCell(c.longTerm)}</td>
                  <td>${rate(c.api570?.longTermRateMmPerYear, c.longTerm)}</td>
                  <td><strong>${formatNumberBR(c.api570?.adoptedRateMmPerYear, 3)}</strong></td>
                  <td>${c.api570 ? this.esc(c.api570.adoptedRateBasis) : "—"}${c.api570?.departsFromConservativeReference ? ` <span class="tag warn">≠ conservadora</span>` : ""}</td>
                  <td>${c.situation === "OK" ? "Calculada" : `<span class="tag ${c.situation === "BELOW_MINIMUM" ? "bad" : "warn"}">${this.esc(AVR_SITUATION_LABELS[c.situation])}</span>`}</td>
                </tr>`;
              }).join("")}
          </tbody>
        </table>
        ${calc.some(c => c.api570!.warnings.length > 0) ? `<div class="hint" style="margin-top:8px;">${calc.flatMap(c => c.api570!.warnings.map(w => `${this.esc(c.pointId)}: ${this.esc(w)}`)).join("<br>")}</div>` : ""}
        ${r.calculatedAt ? `<p class="hint" style="margin-top:8px;">Último cálculo: ${this.esc(new Date(r.calculatedAt).toLocaleString("pt-BR"))}${r.calculationOutdated ? " — DESATUALIZADO" : ""}</p>` : ""}
      </section>`;
  }

  private renderCardLife(r: AvrReport): string {
    const critical = findCriticalPoint(r.components);
    return `
      <section class="avr-card" id="avr-sec-06">
        <div class="card-head"><span class="card-title"><span class="card-num">06.</span> Vida Remanescente</span></div>
        <table class="avr-table">
          <thead><tr><th>Ponto</th><th>Localização</th><th>Longo Prazo</th><th>Curto Prazo</th><th>Esp. Atual</th><th>Data Atual</th><th>E. Mínima</th><th>TC Adotada (motor)</th><th>VR (anos)</th><th>Data prevista</th><th>Situação / Criticidade</th></tr></thead>
          <tbody>
            ${r.components.length === 0
              ? `<tr><td class="empty" colspan="11">Sem cálculo.</td></tr>`
              : r.components.map(c => {
                const last = c.readings[c.readings.length - 1];
                return `<tr>
                  <td><strong>${this.esc(c.pointId)}</strong>${c.component ? `<br><small>${this.esc(c.component)}</small>` : ""}</td>
                  <td>${this.esc(c.location || "—")}</td>
                  <td>${c.longTerm ? `${formatNumberBR(c.longTerm.thicknessMm)}<br><small>${this.esc(formatCivilDate(c.longTerm.date))}</small>` : "—"}</td>
                  <td>${c.shortTerm ? `${formatNumberBR(c.shortTerm.thicknessMm)}<br><small>${this.esc(formatCivilDate(c.shortTerm.date))}</small>` : "—"}</td>
                  <td>${formatNumberBR(last?.thicknessMm)}</td>
                  <td>${last ? this.esc(formatCivilDate(last.date)) : "—"}</td>
                  <td>${formatNumberBR(c.minimumThicknessMm)}<br><small>${this.esc(c.minimumThicknessOrigin || "—")}</small></td>
                  <td>${formatNumberBR(c.api570?.adoptedRateMmPerYear, 3)}</td>
                  <td><strong>${c.situation === "OK" ? formatNumberBR(c.api570?.remainingLifeYears, 1) : "—"}</strong></td>
                  <td>${c.situation === "OK" && c.api570?.estimatedDateAtMinimumThickness ? this.esc(formatCivilDate(c.api570.estimatedDateAtMinimumThickness)) : "—"}</td>
                  <td><span class="crit crit-${c.criticality.toLowerCase()}">${this.esc(AVR_CRITICALITY_LABELS[c.criticality])}</span>${c.situation !== "OK" ? `<br><small class="tag ${c.situation === "BELOW_MINIMUM" ? "bad" : "warn"}">${this.esc(AVR_SITUATION_LABELS[c.situation])}</small>` : ""}</td>
                </tr>`;
              }).join("")}
          </tbody>
        </table>
        ${critical ? `
          <div class="critical-box">
            <strong>PONTO CRÍTICO</strong> — ${this.esc(critical.pointId)}${critical.component ? ` · ${this.esc(critical.component)}` : ""}${critical.location ? ` · ${this.esc(critical.location)}` : ""}:
            E. atual ${formatNumberBR(critical.readings[critical.readings.length - 1]?.thicknessMm)} mm · TC adotada ${formatNumberBR(critical.api570?.adoptedRateMmPerYear, 3)} mm/ano · VR ${formatNumberBR(critical.api570?.remainingLifeYears, 1)} anos · ${this.esc(AVR_CRITICALITY_LABELS[critical.criticality])}.
            <span class="hint">Resumo matemático — a conclusão é do Engenheiro (seção 07).</span>
          </div>` : ""}
      </section>`;
  }

  private renderCardAnalysis(r: AvrReport, ro: boolean): string {
    return `
      <section class="avr-card" id="avr-sec-07">
        <div class="card-head"><span class="card-title"><span class="card-num">07.</span> Análise Técnica e Conclusão de Engenharia</span></div>
        <label class="field"><span>Análise técnica *</span><textarea class="ctl textarea" data-field="analysis" placeholder="Registre a análise do Engenheiro. O sistema não escreve a conclusão." ${this.dis(ro)}>${this.esc(r.analysis)}</textarea></label>
        <div class="grid cols-2" style="margin-top:12px;">
          <label class="field"><span>Status Geral da AVR *</span>
            <select class="ctl" data-concl="status" ${this.dis(ro)} title="Juízo TÉCNICO sobre o equipamento. Não é o status documental do fluxo (rascunho, aguardando PLH, aprovada).">
              <option value="">Selecione…</option>
              ${AVR_GENERAL_STATUS_OPTIONS.map(o => `<option value="${o.value}" ${r.conclusion.status === o.value ? "selected" : ""}>${this.esc(o.label)}</option>`).join("")}
              ${r.conclusion.status && !isAvrGeneralStatus(r.conclusion.status)
                ? `<option value="${this.esc(r.conclusion.status)}" selected>${this.esc(r.conclusion.status)} — valor antigo, selecione um oficial</option>`
                : ""}
            </select>
            <small class="field-hint">Técnico, não documental: uma AVR aprovada pelo PLH pode concluir o equipamento REPROVADO.</small>
          </label>
          <label class="field"><span>Observação</span><input class="ctl" data-concl="observation" value="${this.esc(r.conclusion.observation)}" ${this.dis(ro)}></label>
        </div>
      </section>`;
  }

  private renderCardRecommendations(r: AvrReport, ro: boolean): string {
    const linked = new Set(r.recommendationIds);
    return `
      <section class="avr-card" id="avr-sec-08">
        <div class="card-head"><span class="card-title"><span class="card-num">08.</span> Recomendações <span class="count">${linked.size} vinculada(s)</span></span>
          <button class="btn sm" id="btn-open-recommendations"><i data-lucide="external-link"></i> Abrir módulo de Recomendações</button></div>
        <p class="hint" style="margin:0 0 10px 0;">Recomendações são criadas no módulo oficial (ZR pelo fluxo existente). Aqui só se vincula as deste equipamento.</p>
        <table class="avr-table">
          <thead><tr><th></th><th>ZR</th><th>Status</th><th>Escopo</th></tr></thead>
          <tbody>
            ${this.recommendations.length === 0
              ? `<tr><td class="empty" colspan="4">Nenhuma Recommendation registrada para este equipamento.</td></tr>`
              : this.recommendations.map(rec => `<tr>
                  <td><input type="checkbox" data-rec-id="${this.esc(rec.id)}" ${linked.has(rec.id) ? "checked" : ""} ${this.dis(ro)}></td>
                  <td>${this.esc(rec.zr || "—")}</td><td>${this.esc(rec.status || "—")}</td><td>${this.esc(rec.scope)}</td>
                </tr>`).join("")}
          </tbody>
        </table>
      </section>`;
  }

  private renderCardCroqui(r: AvrReport, ro: boolean): string {
    const withCroqui = r.selectedSources.filter(s => s.hasCroqui);
    return `
      <section class="avr-card" id="avr-sec-09">
        <div class="card-head"><span class="card-title"><span class="card-num">09.</span> Croqui</span>
          ${ro ? "" : `<div style="display:flex; gap:8px; align-items:center;">
            ${withCroqui.length > 0 ? `<select class="ctl sm" id="sel-croqui-source" style="width:auto;">${this.opt("", "Reaproveitar croqui do relatório...", true)}${withCroqui.map(s => this.opt(this.sourceKey(s), `${AVR_SOURCE_KIND_LABELS[s.kind]} ${s.reportNumber}`, false)).join("")}</select>` : ""}
            <input type="file" id="inp-croqui" accept="image/*" style="display:none;">
            <button class="btn sm" id="btn-croqui-upload"><i data-lucide="upload"></i> ${r.croqui ? "Substituir" : "Anexar"}</button>
            ${r.croqui ? `<button class="btn sm danger" id="btn-croqui-remove"><i data-lucide="trash-2"></i> Remover</button>` : ""}
          </div>`}
        </div>
        ${r.croqui
          ? `<div class="croqui-box"><img src="${r.croqui.image}" alt="Croqui" data-lightbox="${this.esc(r.croqui.fileName)}"><p class="hint">${this.esc(r.croqui.fileName)}${r.croqui.sourceReportNumber ? ` — reaproveitado do relatório ${this.esc(r.croqui.sourceReportNumber)}` : ""}</p></div>`
          : `<p class="hint">Nenhum croqui. Reaproveite o croqui de um Relatório ME de origem ou anexe uma imagem.</p>`}
      </section>`;
  }

  private renderCardSignatories(r: AvrReport, ro: boolean, canSign: boolean): string {
    const elab = r.signatories.find(s => s.role === "ELABORADO_POR");
    const plh = r.signatories.find(s => s.role === "PLH");
    const elabCandidates = elaboratorCandidates(this.professionals, elab?.professionalId);
    const plhCandidates = plhCandidatesForClient(this.professionals, r.clientId);
    const card = (role: AvrSignatory["role"], label: string, s: AvrSignatory | undefined, candidates: any[], emptyMsg: string) => {
      const image = s?.professionalId ? this.signatureImages.get(s.professionalId) : null;
      return `
        <div class="sig-card ${ro ? "readonly" : ""}">
          <div class="sig-role">${label}</div>
          ${ro ? "" : `<select class="ctl sm" data-signatory-role="${role}">${this.opt("", candidates.length === 0 ? emptyMsg : "Selecione o Profissional...", !s?.professionalId)}${candidates.map((p: any) => this.opt(p.id, formatSignatoryOption(p), s?.professionalId === p.id)).join("")}</select>`}
          <div class="sig-image">${image ? `<img src="${image}" alt="Assinatura">` : `<span class="sig-blank">${s?.professionalId ? "Profissional sem imagem de assinatura cadastrada" : "—"}</span>`}</div>
          <div class="sig-name">${this.esc(s?.name || "—")}</div>
          <div class="sig-meta">${this.esc(s?.functionTitle || "")}${s?.credential ? ` · ${this.esc(s.credential)}` : ""}</div>
          <div class="sig-meta">${s?.signedAt ? `Assinado em ${this.esc(new Date(s.signedAt).toLocaleString("pt-BR"))}` : ""}</div>
        </div>`;
    };
    return `
      <section class="avr-card" id="avr-sec-10">
        <div class="card-head"><span class="card-title"><span class="card-num">10.</span> Signatários</span>
          ${canSign ? `<button class="btn primary sm" id="btn-sign-plh"><i data-lucide="pen-line"></i> Aprovar e Assinar como PLH</button>` : ""}</div>
        <div class="sig-grid">
          ${card("ELABORADO_POR", "ELABORADO POR", elab, elabCandidates, "Nenhum Profissional ativo")}
          ${card("PLH", "ENGENHEIRO PLH", plh, plhCandidates, "Nenhum PLH habilitado para este Cliente")}
        </div>
        <p class="hint" style="margin-top:8px;">Assinar como PLH exige habilitação real para o Cliente (gate central) e a AVR sem pendências; o documento é congelado nesta revisão.</p>
      </section>`;
  }

  private renderPreviewModal(r: AvrReport): string {
    return `
      <div class="modal-backdrop" id="avr-preview-modal">
        <div class="modal-box preview">
          <div class="modal-head"><span><i data-lucide="file-down"></i> AVR — ${this.esc(r.avrNumber)} · Rev. ${this.esc(r.revision)}</span><button class="icon-btn" id="btn-close-preview">✕</button></div>
          <div style="flex:1; position:relative; overflow:hidden;"><app-pdf-preview id="avr-pdf-preview" style="height:100%; display:block;"></app-pdf-preview></div>
        </div>
      </div>`;
  }

  /** ENVIAR AVR PARA ANÁLISE DO PLH? — resumo do que vai, PLH escolhido pelo Engenheiro (nunca pré-atribuído). */
  private renderSubmitModal(r: AvrReport): string {
    const m = this.submitModal;
    return `
      <div class="modal-backdrop" id="avr-submit-modal">
        <div class="modal-box">
          <div class="modal-head"><span><i data-lucide="send"></i> ENVIAR AVR PARA ANÁLISE DO PLH?</span><button class="icon-btn" id="btn-close-submit">✕</button></div>
          <div class="modal-body">
            <div class="grid cols-2">
              <label class="field"><span>AVR</span><input class="ctl" readonly value="${this.esc(r.avrNumber)} · Rev. ${this.esc(r.revision)}"></label>
              <label class="field"><span>TAG</span><input class="ctl" readonly value="${this.esc(r.equipmentTag || "—")}"></label>
              <label class="field"><span>Relatórios de origem</span><input class="ctl" readonly value="${this.esc(r.selectedSources.map(s => `${AVR_SOURCE_KIND_LABELS[s.kind]} ${s.reportNumber} (Rev. ${s.revision})`).join(", ") || "—")}"></label>
              <label class="field"><span>Pontos analisados</span><input class="ctl" readonly value="${r.components.length} ponto(s) · ${r.readings.length} leitura(s)"></label>
            </div>
            <div class="grid cols-2" style="margin-top:12px;">
              <label class="field"><span>Projeto (fonte dos responsáveis)</span><input class="ctl" readonly value="${this.esc(m.resolved?.project ? `${m.resolved.project.code} — ${m.resolved.project.name}` : "—")}"></label>
              <label class="field"><span>Engenheiro responsável (Projeto)</span><input class="ctl" readonly value="${this.esc(m.resolved?.engineer?.name || "—")}"></label>
              <label class="field"><span>PLH responsável (Cliente)</span><input class="ctl" readonly value="${this.esc(m.resolved?.plh ? `${m.resolved.plh.name}${m.resolved.plh.functionTitle ? " — " + m.resolved.plh.functionTitle : ""}${m.resolved.plh.credential ? " · " + m.resolved.plh.credential : ""}` : "—")}">
                <small class="hint">Definido no cadastro do Cliente e validado pelo gate oficial de PLH. Não há seleção manual.</small></label>
            </div>
            ${m.resolved && !m.resolved.plh ? `<div class="avr-notice avr-notice-error" style="margin-top:8px;">${this.esc(m.resolved.reason || CLIENT_WITHOUT_PLH)}<div style="margin-top:8px;"><button class="btn" id="btn-configure-client-plh">CONFIGURAR PLH DO CLIENTE</button></div></div>` : ""}
            ${r.plhReview ? `<p class="hint" style="margin-top:8px;">Reenvio — ciclo ${r.plhReview.cycle + 1}. O histórico da revisão é preservado (mesmo Nº AVR).</p>` : ""}
            <div class="avr-notice" style="margin-top:14px;">Após o envio, esta revisão ficará em análise do PLH. Alterações deverão retornar pelo fluxo de correção.</div>
            <div class="avr-actions" style="justify-content:flex-end; margin-top:6px;">
              <button class="btn" id="btn-cancel-submit">Cancelar</button>
              <button class="btn workflow" id="btn-confirm-submit" ${m.sending || !m.plhProfessionalId ? "disabled" : ""}><i data-lucide="send"></i> ${m.sending ? "Enviando..." : "Enviar para Análise"}</button>
            </div>
          </div>
        </div>
      </div>`;
  }

  private renderHistoryModal(r: AvrReport): string {
    const entries = [...(r.history || [])].reverse();
    const labels: Record<string, string> = AVR_HISTORY_LABELS;
    return `
      <div class="modal-backdrop" id="avr-history-modal">
        <div class="modal-box">
          <div class="modal-head"><span><i data-lucide="history"></i> Histórico — ${this.esc(r.avrNumber)}</span><button class="icon-btn" id="btn-close-history">✕</button></div>
          <div class="modal-body">
            ${entries.length === 0 ? `<p class="hint">Sem eventos registrados.</p>` : `
              <table class="avr-table"><thead><tr><th>Quando</th><th>Ação</th><th>Detalhe</th><th>Por</th></tr></thead>
              <tbody>${entries.map(h => `<tr><td>${this.esc(new Date(h.at).toLocaleString("pt-BR"))}</td><td><strong>${this.esc(labels[h.action] || h.action)}</strong>${h.fromStatus && h.toStatus ? `<br><small>${this.esc(AVR_STATUS_LABELS[h.fromStatus] || h.fromStatus)} → ${this.esc(AVR_STATUS_LABELS[h.toStatus] || h.toStatus)}</small>` : ""}</td><td>${this.esc(h.detail)}</td><td>${this.esc(h.by)}</td></tr>`).join("")}</tbody></table>`}
          </div>
        </div>
      </div>`;
  }

  private renderLightbox(): string {
    return `
      <div class="modal-backdrop" id="avr-lightbox">
        <div class="modal-box lightbox">
          <div class="modal-head"><span>${this.esc(this.lightbox.title)}</span><button class="icon-btn" id="btn-close-lightbox">✕</button></div>
          <div class="modal-body" style="display:flex; align-items:center; justify-content:center;"><img src="${this.lightbox.src}" alt="${this.esc(this.lightbox.title)}" style="max-width:100%; max-height:78vh; object-fit:contain;"></div>
        </div>
      </div>`;
  }

  // ------------------------------------------------------------- EVENTS

  private attachEvents(ro: boolean, canSources: boolean) {
    this.querySelector("#btn-save-draft")?.addEventListener("click", () => this.onSaveDraft());
    this.querySelector("#btn-refresh-sources")?.addEventListener("click", () => this.onRefreshSources());
    this.querySelector("#btn-calculate")?.addEventListener("click", () => this.onCalculate());
    this.querySelector("#btn-validate")?.addEventListener("click", () => this.onValidate());
    this.querySelector("#btn-preview")?.addEventListener("click", () => this.onPreviewOrPdf(false));
    this.querySelector("#btn-generate-pdf")?.addEventListener("click", () => this.onPreviewOrPdf(true));
    this.querySelector("#btn-history")?.addEventListener("click", () => { this.historyOpen = true; this.render(); });
    this.querySelector("#btn-close-history")?.addEventListener("click", () => { this.historyOpen = false; this.render(); });
    this.querySelector("#btn-close-preview")?.addEventListener("click", () => { this.previewOpen = false; this.render(); });
    this.querySelector("#btn-close-lightbox")?.addEventListener("click", () => { this.lightbox = { open: false, src: "", title: "" }; this.render(); });
    this.querySelector("#btn-sign-plh")?.addEventListener("click", () => this.onSignAsPlh());
    this.querySelector("#btn-submit-plh")?.addEventListener("click", () => this.onSubmitForPlhClick());
    this.querySelector("#btn-delete-avr")?.addEventListener("click", () => { this.deleteModal = true; this.render(); });
    this.querySelector("#btn-close-delete")?.addEventListener("click", () => { this.deleteModal = false; this.render(); });
    this.querySelector("#btn-cancel-delete")?.addEventListener("click", () => { this.deleteModal = false; this.render(); });
    this.querySelector("#btn-confirm-delete")?.addEventListener("click", () => this.onDeleteAvr());
    // Card ANÁLISE DO PLH: leva à tela de análise, onde estão os
    // formulários de parecer, correção por ponto e reprovação.
    this.querySelectorAll("[data-open-avr-review]").forEach(btn => btn.addEventListener("click", () =>
      router.navigate(`/engineering/avr/${(btn as HTMLElement).dataset["openAvrReview"]}`)));
    this.querySelectorAll("[data-point-history]").forEach(btn => btn.addEventListener("click", () => this.openPointHistory((btn as HTMLElement).dataset["pointHistory"]!)));
    this.querySelector("#btn-close-point-history")?.addEventListener("click", () => { this.historyModal.open = false; this.render(); });
    this.querySelector("#btn-cancel-point-history")?.addEventListener("click", () => { this.historyModal.open = false; this.render(); });
    this.querySelector("#btn-save-point-history")?.addEventListener("click", () => this.onSavePointHistory());
    this.querySelectorAll("[data-search-term]").forEach(btn => btn.addEventListener("click", () => { const t = (btn as HTMLElement).dataset["searchTerm"] as "SHORT" | "LONG"; this.historyModal = { ...this.historyModal, search: this.historyModal.search === t ? null : t }; this.render(); }));
    this.querySelectorAll("[data-use-candidate]").forEach(btn => btn.addEventListener("click", () => { const [t, i] = ((btn as HTMLElement).dataset["useCandidate"] || "").split(":"); this.useCandidate(t as "SHORT" | "LONG", Number(i)); }));
    this.querySelectorAll("[data-clear-term]").forEach(btn => btn.addEventListener("click", () => this.clearTerm((btn as HTMLElement).dataset["clearTerm"] as "SHORT" | "LONG")));
    for (const term of ["short", "long"] as const) {
      const thicknessInput = this.querySelector(`#inp-${term}-thickness`) as HTMLInputElement | null;
      // Digitar por cima de um valor vindo de relatório apaga a fonte — passa a ser dado complementar (MANUAL).
      thicknessInput?.addEventListener("input", () => { this.historyModal[term] = { ...this.historyModal[term], thickness: thicknessInput.value, ref: "" }; });
      // Padrão 0,00 ao sair do campo: "6" → "6,00", "6,2" → "6,20".
      thicknessInput?.addEventListener("blur", () => { const f = formatThicknessInput(thicknessInput.value); if (f) { thicknessInput.value = f; this.historyModal[term] = { ...this.historyModal[term], thickness: f }; } });
      this.querySelector(`#inp-${term}-date`)?.addEventListener("change", e => { this.historyModal[term] = { ...this.historyModal[term], date: (e.target as HTMLInputElement).value, ref: "" }; });
    }
    this.querySelector("#btn-close-submit")?.addEventListener("click", () => { this.submitModal = { ...this.submitModal, open: false }; this.render(); });
    this.querySelector("#btn-cancel-submit")?.addEventListener("click", () => { this.submitModal = { ...this.submitModal, open: false }; this.render(); });
    this.querySelector("#btn-configure-client-plh")?.addEventListener("click", () => {
      const clientId = this.report?.clientId;
      if (clientId) openClientPlhSetup(String(clientId));
    });
    this.querySelector("#btn-confirm-submit")?.addEventListener("click", () => this.onConfirmSubmitForPlh());
    this.querySelector("#btn-change-context")?.addEventListener("click", () => this.onChangeContext());
    this.querySelector("#btn-open-recommendations")?.addEventListener("click", () => router.navigate("/integrity/recommendations"));
    this.querySelectorAll(".avr-nav-item").forEach(a => a.addEventListener("click", () => {
      const sec = (a as HTMLElement).dataset["sec"] || "01";
      this.activeSection = sec;
      this.querySelectorAll(".avr-nav-item").forEach(n => n.classList.toggle("active", (n as HTMLElement).dataset["sec"] === sec));
    }));
    this.querySelectorAll("[data-lightbox]").forEach(img => img.addEventListener("click", () => {
      this.lightbox = { open: true, src: (img as HTMLImageElement).src, title: (img as HTMLElement).dataset["lightbox"] || "" };
      this.render();
    }));

    // Filtros da tabela de fontes — só filtram a lista; a seleção pendente permanece.
    this.querySelector("#flt-kind")?.addEventListener("change", e => { this.sourceFilters.kind = (e.target as HTMLSelectElement).value as any; this.render(); });
    this.querySelector("#flt-from")?.addEventListener("change", e => { this.sourceFilters.from = (e.target as HTMLInputElement).value; this.render(); });
    this.querySelector("#flt-to")?.addEventListener("change", e => { this.sourceFilters.to = (e.target as HTMLInputElement).value; this.render(); });
    if (canSources) {
      this.querySelectorAll("[data-source-key]").forEach(cb => cb.addEventListener("change", () => {
        const key = (cb as HTMLElement).dataset["sourceKey"]!;
        if ((cb as HTMLInputElement).checked) this.pendingSources.add(key); else this.pendingSources.delete(key);
        this.render();
      }));
      this.querySelector("#btn-apply-sources")?.addEventListener("click", () => this.onApplySources());
    }

    if (ro) return;
    this.querySelector("#sel-work-order")?.addEventListener("change", e => this.onWorkOrderChange((e.target as HTMLSelectElement).value));
    this.querySelector("#sel-sub-work-order")?.addEventListener("change", e => this.onSubWorkOrderChange((e.target as HTMLSelectElement).value));
    this.querySelectorAll("[data-signatory-role]").forEach(sel => sel.addEventListener("change", e => {
      this.onSignatoryProfessionalChange((sel as HTMLElement).dataset["signatoryRole"] as AvrSignatory["role"], (e.target as HTMLSelectElement).value);
    }));
    this.querySelectorAll("[data-rec-id]").forEach(cb => cb.addEventListener("change", () => {
      this.onToggleRecommendation((cb as HTMLElement).dataset["recId"]!, (cb as HTMLInputElement).checked);
    }));
    this.querySelector("#sel-croqui-source")?.addEventListener("change", e => this.onCroquiFromSource((e.target as HTMLSelectElement).value));
    const croquiInput = this.querySelector("#inp-croqui") as HTMLInputElement | null;
    this.querySelector("#btn-croqui-upload")?.addEventListener("click", () => croquiInput?.click());
    croquiInput?.addEventListener("change", () => { const f = croquiInput.files?.[0]; if (f) this.onCroquiFile(f); });
    this.querySelector("#btn-croqui-remove")?.addEventListener("click", () => { if (this.report) { this.report.croqui = null; this.scheduleAutosave(); this.render(); } });
  }

  private esc(value: unknown): string {
    return String(value ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] || c));
  }

  // -------------------------------------------------------------- STYLES

  private styles(): string {
    return `
      ${PLH_REVIEW_UI_STYLES}
      app-avr-page { display:block; --a-blue:#2F6FD6; --a-blue-soft:rgba(47,111,214,0.16); --a-card:var(--ia-bg-panel, #161A22); --a-card-border:var(--ia-line, #262C38); --a-input:var(--ia-bg-panel, #0F131A); --a-input-border:var(--ia-line, #2B3340); --a-text:var(--ia-fg, #E6E9EF); --a-muted:var(--ia-fg-muted, #8A94A6); --a-label:var(--ia-fg-soft, #A7B0C0); }
      .avr-loading, .avr-empty { display:flex; flex-direction:column; align-items:center; gap:12px; padding:64px 24px; color: var(--a-muted); text-align:center; }
      .avr-empty h2 { margin:0; font-size:1.05rem; color: var(--a-text); }
      .avr-empty .err { color: var(--ia-danger-fg, #fca5a5); margin:0; }
      .avr-header { display:flex; justify-content:space-between; align-items:flex-start; gap:16px; flex-wrap:wrap; margin-bottom:14px; }
      .avr-header h1 { margin:0 0 4px 0; font-size:1.35rem; font-weight:700; color: var(--a-text); }
      .avr-header p { margin:0; font-size:0.84rem; color: var(--a-muted); }
      .avr-actions { display:flex; gap:8px; flex-wrap:wrap; justify-content:flex-end; }
      .btn { display:inline-flex; align-items:center; gap:6px; padding:8px 14px; border-radius:6px; border:1px solid var(--a-input-border); background: var(--ia-bg-panel, #1B212C); color: var(--a-text); font-size:0.84rem; font-weight:600; cursor:pointer; }
      .btn:hover { filter:brightness(1.15); }
      .btn.primary { background: var(--a-blue); border-color: var(--a-blue); color: var(--ia-on-color, #fff); }
      .btn.warn { border-color:#f59e0b; color: var(--ia-warning-fg, #fbbf24); }
      .btn.workflow { background:#7c3aed; border-color:#7c3aed; color:#fff; box-shadow:0 0 0 1px rgba(124,58,237,0.4); }
      .btn.sm { padding:5px 10px; font-size:0.76rem; }
      .btn.danger { border-color:#7f1d1d; color: var(--ia-danger-fg, #fca5a5); }
      .btn[disabled] { opacity:0.5; cursor:not-allowed; }
      .btn svg, .btn i, .icon-btn svg { width:14px; height:14px; }
      .icon-btn { background:transparent; border:none; color: var(--a-muted); cursor:pointer; padding:4px; font-size:1rem; }
      .avr-meta { display:grid; grid-template-columns: repeat(5, 1fr); gap:14px; background: var(--a-card); border:1px solid var(--a-card-border); border-radius:8px; padding:12px 16px; margin-bottom:12px; }
      .meta { display:flex; flex-direction:column; gap:3px; }
      .meta .lbl { font-size:0.64rem; text-transform:uppercase; letter-spacing:0.06em; color: var(--a-muted); }
      .meta .val { font-size:0.92rem; font-weight:700; color: var(--a-text); }
      .status { display:inline-block; width:max-content; padding:3px 10px; border-radius:4px; font-size:0.7rem; font-weight:700; text-transform:uppercase; letter-spacing:0.03em; }
      .status-draft { background: rgba(250,204,21,0.18); color: var(--ia-warning-fg, #facc15); }
      .status-calculated { background: rgba(96,165,250,0.18); color: var(--ia-info-fg, #93c5fd); }
      .status-outdated { background: rgba(239,68,68,0.18); color: var(--ia-danger-fg, #fca5a5); }
      .status-validated { background: rgba(96,165,250,0.28); color: var(--ia-info-fg, #bfdbfe); }
      .status-signed { background: rgba(74,222,128,0.18); color: var(--ia-success-fg, #86efac); }
      .status-review { background: rgba(124,58,237,0.22); color: var(--ia-info-fg, #c4b5fd); }
      .status-correction { background: rgba(245,158,11,0.22); color: var(--ia-warning-fg, #fbbf24); }
      .status-approved { background: rgba(16,185,129,0.22); color: var(--ia-success-fg, #6ee7b7); }
      .avr-notice-correction { background: rgba(245,158,11,0.1); border-color: rgba(245,158,11,0.45); color: var(--ia-warning-fg, #fcd34d); }
      .avr-notice { padding:10px 14px; border-radius:6px; font-size:0.82rem; margin-bottom:12px; background: rgba(96,165,250,0.1); border:1px solid rgba(96,165,250,0.35); color: var(--ia-info-fg, #93c5fd); }
      .avr-notice-ok { background: rgba(74,222,128,0.1); border-color: rgba(74,222,128,0.35); color: var(--ia-success-fg, #86efac); }
      .avr-notice-error { background: rgba(239,68,68,0.1); border-color: rgba(239,68,68,0.35); color: var(--ia-danger-fg, #fca5a5); }
      .avr-notice ul { margin:6px 0 0 0; padding-left:18px; }
      .avr-layout { display:grid; grid-template-columns: 200px 1fr; gap:16px; align-items:start; }
      .avr-nav { display:flex; flex-direction:column; gap:4px; position:sticky; top:12px; }
      .avr-nav-item { display:flex; align-items:center; gap:10px; padding:9px 12px; border-radius:6px; background: var(--a-card); border:1px solid var(--a-card-border); color: var(--a-label); font-size:0.78rem; text-decoration:none; cursor:pointer; }
      .avr-nav-item .num { font-weight:800; font-size:0.72rem; color: var(--a-muted); }
      .avr-nav-item.active { background: var(--a-blue); border-color: var(--a-blue); color: var(--ia-on-color, #fff); }
      .avr-nav-item.active .num { color: var(--ia-fg, #fff); }
      .avr-main { display:flex; flex-direction:column; gap:14px; min-width:0; }
      .avr-card { background: var(--a-card); border:1px solid var(--a-card-border); border-radius:8px; padding:16px; }
      .card-head { display:flex; justify-content:space-between; align-items:center; gap:10px; margin-bottom:12px; flex-wrap:wrap; }
      .card-title { font-size:0.86rem; font-weight:700; color: var(--ia-info-fg, #8FB4F0); text-transform:uppercase; letter-spacing:0.03em; display:flex; align-items:center; gap:8px; }
      .card-num { color: var(--ia-info-fg, #8FB4F0); }
      .card-title .count { background: var(--a-blue-soft); color: var(--ia-info-fg, #93c5fd); border-radius:10px; padding:1px 8px; font-size:0.7rem; text-transform:none; }
      .grid { display:grid; gap:12px; }
      .grid.cols-2 { grid-template-columns: repeat(2, minmax(0,1fr)); }
      .grid.cols-4 { grid-template-columns: repeat(4, minmax(0,1fr)); }
      .field { display:flex; flex-direction:column; gap:5px; font-size:0.72rem; color: var(--a-label); min-width:0; }
      .ctl { background: var(--a-input); border:1px solid var(--a-input-border); border-radius:6px; padding:8px 10px; color: var(--a-text); font-size:0.82rem; width:100%; box-sizing:border-box; }
      .ctl:focus { outline:none; border-color: var(--a-blue); }
      .ctl[readonly] { background: var(--ia-bg-panel, #121620); color: var(--ia-fg-soft, #B9C2D0); cursor:default; }
      .ctl:disabled { opacity:0.6; cursor:not-allowed; }
      .ctl.sm { padding:6px 8px; font-size:0.76rem; }
      .ctl.textarea { min-height:140px; resize:vertical; font-family:inherit; line-height:1.5; }
      .ctl.textarea.sm { min-height:80px; }
      .hint { font-size:0.68rem; color: var(--a-muted); }
      .avr-table { width:100%; border-collapse:collapse; font-size:0.76rem; }
      .avr-table th { text-align:left; padding:8px; background: var(--ia-bg-panel, #0F131A); color: var(--a-muted); font-size:0.64rem; text-transform:uppercase; letter-spacing:0.04em; white-space:nowrap; }
      .avr-table td { padding:6px 8px; border-top:1px solid var(--a-card-border); vertical-align:top; color: var(--a-text); }
      .avr-table td.empty { text-align:center; color: var(--a-muted); padding:20px; }
      .avr-table small { color: var(--a-muted); }
      .tag { display:inline-block; padding:1px 7px; border-radius:4px; font-size:0.64rem; font-weight:700; text-transform:uppercase; }
      .tag.ok { background: rgba(74,222,128,0.18); color: var(--ia-success-fg, #86efac); }
      .tag.bad { background: rgba(239,68,68,0.2); color: var(--ia-danger-fg, #fca5a5); }
      .tag.warn { background: rgba(245,158,11,0.2); color: var(--ia-warning-fg, #fbbf24); }
      .crit { display:inline-block; padding:2px 8px; border-radius:4px; font-size:0.68rem; font-weight:800; }
      .crit-low { background:#4caf50; color:#fff; } .crit-moderate { background:#ffc107; color:#333; } .crit-high { background:#ff9800; color:#fff; } .crit-critical { background:#f44336; color:#fff; } .crit-unclassified { background:#e0e0e0; color:#333; }
      .critical-box { margin-top:12px; padding:10px 14px; border-radius:6px; background: rgba(239,68,68,0.08); border:1px solid rgba(239,68,68,0.3); color: var(--a-text); font-size:0.8rem; display:flex; flex-direction:column; gap:4px; }
      .croqui-box { display:flex; flex-direction:column; align-items:center; gap:6px; }
      .croqui-box img { max-width:100%; max-height:360px; border-radius:6px; border:1px solid var(--a-card-border); cursor:zoom-in; background:#fff; }
      .sig-grid { display:grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap:14px; }
      .sig-card { background: var(--ia-bg-panel, #0F131A); border:1px solid var(--a-card-border); border-radius:8px; padding:14px; display:flex; flex-direction:column; gap:6px; align-items:center; text-align:center; }
      .sig-role { font-size:0.7rem; font-weight:800; text-transform:uppercase; letter-spacing:0.05em; color: var(--a-label); }
      .sig-image { height:64px; width:100%; display:flex; align-items:center; justify-content:center; background:#fff; border-radius:6px; overflow:hidden; }
      .sig-image img { max-height:56px; max-width:90%; object-fit:contain; }
      .sig-blank { color: var(--ia-fg-muted, #6b7280); font-size:0.68rem; font-style:italic; }
      .sig-name { font-size:0.86rem; font-weight:700; color: var(--a-text); margin-top:4px; }
      .sig-meta { font-size:0.72rem; color: var(--a-muted); }
      .sig-card.readonly { border-style:dashed; }
      .modal-backdrop { position:fixed; inset:0; background:rgba(0,0,0,0.82); display:flex; align-items:center; justify-content:center; z-index:10000; padding:20px; }
      .modal-box { background: var(--a-card); border:1px solid var(--a-card-border); border-radius:12px; width:100%; max-width:760px; max-height:92vh; display:flex; flex-direction:column; overflow:hidden; }
      .modal-box.preview { max-width:1200px; height:92vh; }
      .modal-box.lightbox { max-width:1000px; }
      .modal-head { padding:12px 18px; border-bottom:1px solid var(--a-card-border); display:flex; justify-content:space-between; align-items:center; color: var(--a-text); font-weight:600; }
      .modal-head span { display:flex; align-items:center; gap:8px; }
      .modal-body { padding:16px 18px; overflow:auto; }
      @media (max-width: 1100px) { .grid.cols-4 { grid-template-columns: repeat(2, minmax(0,1fr)); } .avr-meta { grid-template-columns: repeat(3, 1fr); } }
      @media (max-width: 800px) { .avr-layout { grid-template-columns: 1fr; } .avr-nav { position:static; flex-direction:row; flex-wrap:wrap; } .sig-grid, .grid.cols-2 { grid-template-columns: 1fr; } .avr-meta { grid-template-columns: repeat(2, 1fr); } }
      @media (max-width: 560px) { .grid.cols-4 { grid-template-columns: 1fr; } }
    `;
  }
}

if (!customElements.get("app-avr-page")) {
  customElements.define("app-avr-page", AppAvrPage);
}
